import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { PLOW_TOUR_ID, plowTourSteps } from './plowTour';
import { DEFAULT_SHIFT_START, DEFAULT_SHIFT_END, isShiftMove } from '../../utils/shiftMove';
import { sectionLabelFor } from '../../utils/plowSections';
import { groupAssignments } from '../../utils/plowDayLayout';
import { standbySlotFor, standbyPay, STANDBY_RATE_PER_HOUR } from '../../utils/standby';
import { pickDefaultPlow } from '../../utils/plowDefaultBoard';
import { monthWeekStarts, shiftMonth, myMonthAvailable } from '../../utils/plowMonth';
import { plowRowTone, PLOW_WEEKEND_WASH } from '../../utils/plowRowTone';
import { groupExportTasks, buildExportRows } from '../../utils/plowExport';
import { downloadPlowExport } from './plowExportSheet';
import { downloadXlsx } from '../../utils/xlsxWriter';
import StandbyCalendarModal from './StandbyCalendarModal';
import { buildStandbyRota, renderStandbyRotaHtml, standbyRotaFilename, defaultStandbyRotaRange, fmtLong as fmtLongDate, buildOnCallUpload, onCallUploadSheet, onCallUploadFilename, defaultOnCallLevels } from '../../utils/standbyRotaExport';
import Avatar from '../shared/Avatar';

// ─── PLOW (Plan of the Week) ──────────────────────────────────────────────────
//
// The weekly assignment boards that replace the "AV PLOW & WIP" spreadsheet.
// A board is people against days for one week; each cell splits into the
// board's configured sections (Day, AM/PM, Shift time) and takes a job from
// the board's reusable library or a built-in status. Editors assign; viewers
// and members read. Weeks are fetched one at a time by their Monday date, so
// the past is browsable in place rather than copied to an archive sheet.

// The tinted-badge families that have explicit .dark overrides in styles.css.
const TONES = {
  gray:   'bg-gray-100 text-gray-700',
  blue:   'bg-blue-50 text-blue-700',
  green:  'bg-green-50 text-green-700',
  amber:  'bg-amber-50 text-amber-700',
  red:    'bg-red-50 text-red-700',
  purple: 'bg-purple-50 text-purple-700',
  teal:   'bg-teal-50 text-teal-700',
};
const TONE_KEYS = Object.keys(TONES);
// Absences the server derives from bank holidays, company closures and
// approved leave (see plowAbsences.js). Planned time off is amber like the
// Holiday job; sickness is red because it is the one that lands on a week
// already planned and leaves work needing cover.
const ABSENCE_TONES = {
  bank_holiday:    'bg-amber-50 text-amber-700',
  company_holiday: 'bg-amber-50 text-amber-700',
  leave:           'bg-amber-50 text-amber-700',
  sickness:        'bg-red-50 text-red-700',
  // Days Settings blocks somebody from this board: not time off, so grey.
  blocked:         'bg-gray-100 text-gray-600',
};
// Solid swatches for the tone picker, so the choice is visible before it is used.
const TONE_DOTS = {
  gray: 'bg-gray-400', blue: 'bg-blue-500', green: 'bg-green-500', amber: 'bg-amber-500',
  red: 'bg-red-500', purple: 'bg-purple-500', teal: 'bg-teal-500',
};

// The traffic light on an assignment: tentative or at-risk bookings carry a
// flag that shows on the grid. Solid mid-tone colours read in both modes. The
// grid draws it as the edge of the cell when the whole day is one job, or as
// the border around each task when the day is split; the editor and detail
// views use the dot.
const RAG = {
  red:   { dot: 'bg-red-500', border: 'border-red-500', ring: 'ring-red-500', label: 'Red: at risk' },
  amber: { dot: 'bg-amber-400', border: 'border-amber-400', ring: 'ring-amber-400', label: 'Amber: tentative' },
  green: { dot: 'bg-green-500', border: 'border-green-500', ring: 'ring-green-500', label: 'Green: confirmed' },
};
// A task with no light set still gets a box, so the sections of a mixed day
// read as sections; a quiet grey keeps it from competing with the lights.
const NO_RAG_BORDER = 'border-gray-200';

// ── Date helpers (UTC string arithmetic, like the rest of the app) ────────────
const todayIso = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().slice(0, 10);
};
const weekMonday = (iso) => {
  const d = new Date(`${iso || todayIso()}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmtDay = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
const fmtShort = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtWeek = (iso) => `WC ${new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}`;
const isWeekendIso = (iso) => [0, 6].includes(new Date(`${iso}T00:00:00Z`).getUTCDay());

const personLabel = (m) => m.display_label || m.person_name || m.label || '?';

// A board row is a person, or a label ("SERVICING") standing for work that
// belongs to nobody in particular. One key covers both, so the grid and the
// modals address a row the same way whichever it is. A person's day is a cell
// per section; a label row's day is a list of tasks (data.label_tasks) with
// no sections at all, because a SERVICING lane is however many calls there
// are that day, not an AM and a PM. A label row has no person id, which is
// also why it cannot be put on standby, given a shift time or linked to
// somebody.
const isLabelRow = (m) => !!m && !m.person_id;
const rowKey = (m) => m.person_id || m.id;
const slotRowKey = (s) => s.person_id || s.member_id;

// One row's day as SlotCell takes it: each section's slot with its job and
// the day's desk move or working away record. `slotAt(sectionId)` finds the
// raw slot, so the week grid can use its index and the month view a scan.
function daySlots(data, slotAt, jobById) {
  const slots = {};
  for (const sec of data.sections) {
    const s = slotAt(sec.id);
    if (s) slots[sec.id] = {
      ...s,
      job: s.job_id ? jobById[s.job_id] : null,
      // The day's desk move allowance, if a move job put one there.
      desk_move: (data.desk_move_allowances || []).find(a => a.person_id === s.person_id && a.date === s.slot_date) || null,
      // The day's working away record, if an away job put one there.
      working_away: (data.working_away_records || []).find(a => a.person_id === s.person_id && a.date === s.slot_date) || null,
    };
  }
  return slots;
}

// Module-level on purpose: defined inside its parent, React treats it as a new
// component type on every parent render and remounts it, which wipes the
// dropdown's own picked-but-not-added state mid-use.
function PersonAdder({ people, exclude, onAdd, label }) {
  const [pick, setPick] = useState('');
  const options = people.filter(p => !exclude.includes(p.id));
  return (
    <div className="flex gap-2 items-center">
      <select className="form-select flex-1" value={pick} onChange={e => setPick(e.target.value)}>
        <option value="">{label}</option>
        {options.map(p => <option key={p.id} value={p.id}>{`${p.first_name || ''} ${p.last_name || ''}`.trim()}</option>)}
      </select>
      <button
        type="button"
        className="px-2.5 py-1.5 text-[10px] font-bold text-navy border border-navy rounded hover:bg-blue-50 disabled:opacity-40"
        disabled={!pick}
        onClick={() => { onAdd(pick); setPick(''); }}
      >Add</button>
    </div>
  );
}

// ── One cell of the grid ──────────────────────────────────────────────────────
// `absence` is the derived day off for this person and date, if any:
// { kind, label, slot: 'full' | 'am' | 'pm' }. It is drawn OVER the cell and an
// assigned job stays visible underneath, struck through, so a sick day arriving
// after the week was planned reads as work to reassign rather than as work
// that disappeared. A half-day only covers the section whose label matches it.
// `hints` draws the "+" on an empty assignment row: a bookable-slot hint for
// editors in expanded view. Without it an empty row takes no height, so a
// board with several assignment rows reads compactly.
function SlotCell({ sections, slots, isToday, weekend, clickable, onClick, absence, hints }) {
  const covers = (sec) => !!absence && (absence.slot === 'full' || String(sec.label || '').trim().toLowerCase() === absence.slot);
  // One job across every assignment section reads once, and the cell's own
  // edge takes its traffic light (an inset ring, so the grid's hairlines and
  // layout do not move). Different tasks each get their own box instead.
  // An absence keeps the sections apart so a half-day strike-through lands on
  // the half it covers.
  const grouped = absence ? { collapsed: false, rag: null } : groupAssignments(sections, slots);
  const { collapsed, rag: dayRag } = grouped;
  const firstAssignment = sections.find(s => s.kind === 'assignment');
  const collapsedSlot = collapsed ? grouped.items[0].slot : null;
  const absenceChip = (
    <span className={`text-[9px] font-semibold rounded px-1 py-0.5 truncate ${ABSENCE_TONES[absence?.kind] || ABSENCE_TONES.leave}`} title={absence?.label}>
      {absence?.label}
    </span>
  );
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onClick}
      className={`w-full h-full min-w-0 overflow-hidden min-h-[42px] px-1 py-1 border-r border-b border-gray-200 text-left
        flex flex-col gap-0.5 justify-center transition-colors
        ${weekend ? PLOW_WEEKEND_WASH : 'bg-transparent'}
        ${clickable ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-default'}
        ${dayRag ? `ring-2 ring-inset ${RAG[dayRag]?.ring}` : isToday ? 'ring-1 ring-inset ring-blue-400' : ''}`}
      title={dayRag ? RAG[dayRag]?.label : undefined}
    >
      {absence?.slot === 'full' && <div className="flex items-center gap-1 min-w-0">{absenceChip}</div>}
      {sections.map(sec => {
        if (collapsed && sec.kind === 'assignment' && sec !== firstAssignment) return null;
        const slot = collapsed && sec === firstAssignment ? collapsedSlot : slots[sec.id];
        const covered = covers(sec);
        if (covered && !slot?.job && !slot?.note) {
          // A half-day shows its chip against its own section; a full day's
          // chip already sits above, so an empty covered row adds nothing.
          if (absence.slot === 'full') return null;
          return (
            <div key={sec.id} className="flex items-center gap-1 min-w-0">
              <span className="text-[8px] font-bold text-gray-400 w-6 shrink-0">{sec.label}</span>
              {absenceChip}
            </div>
          );
        }
        if (covered) {
          const name = sec.kind === 'text' ? (slot?.note || '')
            : sec.kind === 'shift_time' ? (slot?.shift_start ? `${slot.shift_start} to ${slot.shift_end}` : '')
            : sec.kind === 'person' ? (slot?.linked_person_name || '')
            : sec.kind === 'standby' ? (slot?.standby_level_name || '')
            : (slot?.job?.name || '');
          return (
            <div key={sec.id} className="flex flex-col gap-0.5 min-w-0">
              {absence.slot !== 'full' && (
                <div className="flex items-center gap-1 min-w-0">
                  <span className="text-[8px] font-bold text-gray-400 w-6 shrink-0">{sec.label}</span>
                  {absenceChip}
                </div>
              )}
              <div className="flex items-center gap-1 min-w-0" title={`${name} ${absence.kind === 'blocked' ? 'is on a blocked day' : `was assigned before ${absence.label.toLowerCase()}`}${clickable ? '. Click to reassign it.' : ''}`}>
                {absence.slot === 'full' && sections.filter(s => s.kind === 'assignment').length > 1 && (
                  <span className="text-[8px] font-bold text-gray-400 w-6 shrink-0">{sec.label}</span>
                )}
                <span className="text-[9px] rounded px-1 py-0.5 truncate line-through text-gray-400 bg-gray-50 border border-gray-200">
                  {name}{slot?.note && sec.kind !== 'text' ? ` · ${slot.note}` : ''}
                </span>
                {clickable && <span className="text-[8px] font-bold text-red-600 shrink-0">Reassign</span>}
              </div>
            </div>
          );
        }
        if (sec.kind === 'text') {
          return (
            <div key={sec.id} className="text-[9px] font-bold text-gray-500 italic truncate leading-tight px-0.5">
              {slot?.note || ''}
            </div>
          );
        }
        if (sec.kind === 'person') {
          return (
            <div key={sec.id} className="text-[9px] font-bold text-gray-500 truncate leading-tight px-0.5" title={slot?.linked_person_name || ''}>
              {slot?.linked_person_name || ''}
            </div>
          );
        }
        if (sec.kind === 'standby') {
          if (!slot?.standby_level_id) return <div key={sec.id} className="text-[9px] text-gray-300 leading-tight px-0.5">{slot?.note || ''}</div>;
          const st = slot.standby_status;
          const badge = st === 'approved' ? ['confirmed', 'bg-emerald-100 text-emerald-800']
            : st === 'pending' ? ['pending', 'bg-amber-100 text-amber-800']
            : st ? [st, 'bg-gray-100 text-gray-500'] : null;
          return (
            <div key={sec.id} className="flex items-center gap-1 min-w-0 px-0.5" title={`Standby: ${slot.standby_level_name || ''}${slot.standby_hours != null ? `, ${slot.standby_hours}h` : ''}${st ? `, ${st}` : ''}`}>
              <span className="text-[9px] font-bold text-purple-700 leading-tight truncate">{slot.standby_level_name || 'Standby'}</span>
              {badge && <span className={`text-[7px] font-bold uppercase rounded px-1 shrink-0 ${badge[1]}`}>{badge[0]}</span>}
            </div>
          );
        }
        if (sec.kind === 'shift_time') {
          // An empty cell means the standard shift; only a moved one is worth a mark.
          if (!slot?.shift_start) return <div key={sec.id} className="text-[9px] text-gray-300 leading-tight px-0.5">{slot?.note || ''}</div>;
          const moved = isShiftMove(slot.shift_start, slot.shift_end);
          return (
            <div key={sec.id} className="flex items-center gap-1 min-w-0 px-0.5" title={moved ? 'Outside 7am to 6pm: a shift move at +25%' : 'Within usual hours'}>
              <span className={`text-[9px] font-bold leading-tight truncate ${moved ? 'text-amber-700' : 'text-gray-500'}`}>
                {slot.shift_start} to {slot.shift_end}
              </span>
              {moved && <span className="text-[7px] font-bold uppercase bg-amber-100 text-amber-800 rounded px-1 shrink-0">moved</span>}
              {slot.note && <span className="text-[8px] text-gray-400 italic truncate">{slot.note}</span>}
            </div>
          );
        }
        const many = sections.filter(s => s.kind === 'assignment').length > 1;
        if (!slot?.job) {
          if (!hints) return null;
          return (
            <div key={sec.id} className="flex items-center gap-1 min-h-[14px]">
              {!collapsed && many && <span className="text-[8px] font-bold text-gray-400 w-6 shrink-0">{sec.label}</span>}
              <span className="text-[9px] text-gray-300">+</span>
            </div>
          );
        }
        // The day's border already carries a collapsed day's light; a task on
        // a mixed day is boxed in its own.
        const boxed = !collapsed;
        return (
          <div key={sec.id}
            className={`flex gap-1 min-w-0 ${collapsed ? 'flex-col items-start' : 'items-center'} ${boxed ? `rounded border-2 px-0.5 ${RAG[slot.rag]?.border || NO_RAG_BORDER}` : ''}`}
            title={boxed && slot.rag ? RAG[slot.rag]?.label : undefined}>
            {!collapsed && many && <span className="text-[8px] font-bold text-gray-400 w-6 shrink-0">{sec.label}</span>}
            {/* A collapsed day has the room the second row used to take, so
                the job name wraps and the AV reference sits under it rather
                than both being cut short in one chip. The wrap is capped at
                three lines: a long name in a narrow weekend column would
                otherwise run to a dozen, and every weekday cell on that row
                grows to match it. The full name stays in the tooltip, and the
                reference sits outside the clamp so it is never what is cut. */}
            <span
              className={`text-[9px] font-semibold rounded px-1 py-0.5 min-w-0 ${collapsed ? 'whitespace-normal break-words' : 'truncate'} ${TONES[slot.job.tone] || TONES.blue}`}
              title={[slot.job.name, slot.job.sow_reference, slot.note, slot.rag && RAG[slot.rag]?.label].filter(Boolean).join(' · ')}
            >
              {collapsed ? (
                <span className="line-clamp-3">
                  {slot.job.name}{slot.note ? ` · ${slot.note}` : ''}
                </span>
              ) : (
                <>{slot.job.name}{slot.note ? ` · ${slot.note}` : ''}</>
              )}
              {collapsed && slot.job.sow_reference && <span className="block font-bold opacity-80">{slot.job.sow_reference}</span>}
            </span>
            {/* The overtime designators. A collapsed day stacks them under the
                job so the wrapped name cannot push them off the edge. Before a
                day's allowance record exists the job's own flag shows, as in
                the job library, so an away or move job is never plain here. */}
            {(!!slot.job.is_desk_move || !!slot.job.is_working_away) && (
              <span className="flex items-center gap-1 shrink-0">
            {!!slot.job.is_desk_move && !slot.desk_move && (
              <span className="text-[7px] font-bold uppercase rounded px-1 shrink-0 bg-amber-100 text-amber-800" title="Desk move: raises the allowance when saved">move</span>
            )}
            {!!slot.job.is_working_away && !slot.working_away && (
              <span className="text-[7px] font-bold uppercase rounded px-1 shrink-0 bg-sky-100 text-sky-800" title="Working away: pays the +25% uplift when saved">away</span>
            )}
            {/* A move job's allowance for the day: £20 approved on save for a
                weekend, £15 pending the line manager for a weekday. */}
            {!!slot.job.is_desk_move && slot.desk_move && (
              <span
                className={`text-[7px] font-bold rounded px-1 shrink-0 ${slot.desk_move.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                title={`Desk move allowance ${slot.desk_move.status === 'approved' ? 'approved' : 'awaiting the line manager (was the move past 4pm?)'}`}
              >
                £{slot.desk_move.request_type === 'desk_move_weekend' ? 20 : 15}{slot.desk_move.status === 'approved' ? ' ✓' : ' pending'}
              </span>
            )}
            {/* An away job's uplift for the day: a quarter of the hourly rate on
                the shift, approved on save (Overtime Policy 2.a.vi). */}
            {!!slot.job.is_working_away && slot.working_away && (
              <span
                className="text-[7px] font-bold rounded px-1 shrink-0 bg-sky-100 text-sky-800"
                title={`Working away: +25% on a ${slot.working_away.hours}h shift${slot.working_away.status === 'approved' ? ', approved' : ', pending'}`}
              >
                away {slot.working_away.hours}h
              </span>
            )}
              </span>
            )}
          </div>
        );
      })}
    </button>
  );
}

// ── One day of a label row: the task list ─────────────────────────────────────
// A label row has no sections, so its cell is the list of tasks for the day in
// the order the planner put them, each boxed in its own traffic light like a
// task on a mixed day. There is no limit: the row grows to fit.
function LabelTaskCell({ tasks, jobById, isToday, weekend, clickable, onClick, hints }) {
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onClick}
      className={`w-full h-full min-w-0 overflow-hidden min-h-[42px] px-1 py-1 border-r border-b border-gray-200 text-left
        flex flex-col gap-0.5 justify-center transition-colors
        ${weekend ? PLOW_WEEKEND_WASH : 'bg-transparent'}
        ${clickable ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-default'}
        ${isToday ? 'ring-1 ring-inset ring-blue-400' : ''}`}
    >
      {tasks.map(t => {
        const job = t.job_id ? jobById[t.job_id] : null;
        const name = job ? job.name : (t.note || '');
        return (
          <div
            key={t.id}
            className={`flex items-center gap-1 min-w-0 rounded border-2 px-0.5 ${RAG[t.rag]?.border || NO_RAG_BORDER}`}
            title={[job?.name, job?.sow_reference, t.note, t.rag && RAG[t.rag]?.label].filter(Boolean).join(' · ')}
          >
            {job ? (
              <span className={`text-[9px] font-semibold rounded px-1 py-0.5 min-w-0 truncate ${TONES[job.tone] || TONES.blue}`}>
                {job.name}{t.note ? ` · ${t.note}` : ''}
              </span>
            ) : (
              <span className="text-[9px] font-bold text-gray-500 italic truncate leading-tight px-0.5">{name}</span>
            )}
          </div>
        );
      })}
      {!tasks.length && hints && <span className="text-[9px] text-gray-300 min-h-[14px]">+</span>}
    </button>
  );
}

// Typing the SOW before the job name: look the reference up and offer the
// SOW's title as the name. The caller only applies it while the name is still
// empty, and it lands in an ordinary input, so it stays editable.
async function suggestJobName(ref) {
  const wanted = (ref || '').trim();
  if (!wanted) return null;
  try {
    const r = await api.get('sows', { reference: wanted });
    const list = Array.isArray(r) ? r : (r?.entries || []);
    return (list[0]?.detail || '').trim() || null;
  } catch {
    return null;
  }
}

// ── The cell editor: one person, one day, every section at once ───────────────
// People only: a label row's day is a task list and has its own editor below.
function CellEditorModal({ data, rowId, date, people, onClose, onSaved, onRefresh }) {
  const member = data.members.find(m => rowKey(m) === rowId);
  const editableSections = data.sections;
  const statuses = data.jobs.filter(j => j.kind === 'status' && j.active);
  const workJobs = data.jobs.filter(j => j.kind === 'work' && j.active);
  const initial = useMemo(() => {
    const out = {};
    for (const sec of editableSections) {
      const slot = data.slots.find(s => slotRowKey(s) === rowId && s.slot_date === date && s.section_id === sec.id);
      // A shift time row starts from the 8am to 4pm nearly every shift is, so a
      // planner only touches it when the shift really has moved. The default is
      // not saved unless it is changed: a standard shift needs no record.
      out[sec.id] = {
        job_id: slot?.job_id || '', note: slot?.note || '', rag: slot?.rag || '',
        shift_start: slot?.shift_start || (sec.kind === 'shift_time' ? DEFAULT_SHIFT_START : ''),
        shift_end: slot?.shift_end || (sec.kind === 'shift_time' ? DEFAULT_SHIFT_END : ''),
        linked_person_id: slot?.linked_person_id || '',
        standby_level_id: slot?.standby_level_id || '',
      };
    }
    return out;
  }, [data, editableSections, rowId, date]);
  const [values, setValues] = useState(initial);
  const [extraDays, setExtraDays] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // "New job" opens a small form under one section, so a job that is not in
  // the library yet can be made here and assigned in the same visit rather
  // than by closing this, opening the library, and coming back.
  const [newJobFor, setNewJobFor] = useState(null);
  const [newJob, setNewJob] = useState({ name: '', tone: 'blue', sowRef: '' });
  const [creating, setCreating] = useState(false);
  // "Copy from": people work in pairs, so another member's day for the same
  // date can be taken as the starting point. Filled from the week already
  // loaded; the result is ordinary editor state, so it can be adjusted
  // before it is saved, and nothing is written until Save.
  const [copiedFrom, setCopiedFrom] = useState(null);
  const [copyPickerOpen, setCopyPickerOpen] = useState(false);
  // "Copy to": the other direction. A day planned once is often several
  // people's day, so the ticked rows are written the same cell on Save. It is
  // the people axis of "Also apply to", which is the days axis, and the two
  // combine: three people across two days is one Save.
  const [copyTo, setCopyTo] = useState(() => new Set());
  const [copyToOpen, setCopyToOpen] = useState(false);
  const { toast } = useToast();

  const setVal = (secId, patch) => setValues(v => ({ ...v, [secId]: { ...v[secId], ...patch } }));

  // Other people only: a label row's day is a list rather than sections, so
  // there is nothing section-shaped to copy either way.
  const copyCandidates = data.members.filter(m => m.active && m.person_id && rowKey(m) !== rowId);
  const copyFrom = (fromId) => {
    setError('');
    if (!fromId) return;
    const source = data.members.find(m => rowKey(m) === fromId);
    const next = {};
    let found = false;
    for (const sec of editableSections) {
      const slot = data.slots.find(s => slotRowKey(s) === fromId && s.slot_date === date && s.section_id === sec.id);
      if (slot && (slot.job_id || slot.note || slot.shift_start || slot.linked_person_id || slot.standby_level_id)) found = true;
      next[sec.id] = {
        job_id: slot?.job_id || '', note: slot?.note || '', rag: slot?.rag || '',
        shift_start: slot?.shift_start || (sec.kind === 'shift_time' ? DEFAULT_SHIFT_START : ''),
        shift_end: slot?.shift_end || (sec.kind === 'shift_time' ? DEFAULT_SHIFT_END : ''),
        linked_person_id: slot?.linked_person_id || '',
        standby_level_id: slot?.standby_level_id || '',
      };
    }
    if (!found) {
      setError(`${personLabel(source || {})} has nothing assigned on ${fmtDay(date)}, so there is nothing to copy`);
      return;
    }
    setValues(next);
    setCopiedFrom(source || null);
    setCopyPickerOpen(false);
  };

  const openNewJob = (secId) => {
    setError('');
    setNewJob({ name: '', tone: 'blue', sowRef: '' });
    setNewJobFor(prev => (prev === secId ? null : secId));
  };

  const prefillNewJobName = async () => {
    if (newJob.name.trim()) return;
    const name = await suggestJobName(newJob.sowRef);
    if (name) setNewJob(v => (v.name.trim() ? v : { ...v, name }));
  };

  const createJob = async () => {
    setError('');
    if (!newJob.name.trim()) { setError('Give the job a name'); return; }
    setCreating(true);
    try {
      const created = await api.post(`plows/${data.plow.id}/jobs`, {
        name: newJob.name.trim(), tone: newJob.tone, sow_reference: newJob.sowRef.trim(),
      });
      // Reload the week so the new job is in the selector, then pick it for
      // the section the form was opened under.
      if (onRefresh) await onRefresh();
      setVal(newJobFor, { job_id: created.id });
      setNewJobFor(null);
      toast.success(`"${newJob.name.trim()}" added to the job library`);
    } catch (e) {
      setError(e.message || 'The job could not be created');
    }
    setCreating(false);
  };
  const toggleCopyTo = (id) => setCopyTo(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // Standby and a shift time are pay, not just a plan: a copy raises one of
  // each per person. Said at the picker, so nobody ticks five people without
  // knowing what they are committing to.
  const paySections = editableSections.filter(s => s.kind === 'standby' || s.kind === 'shift_time');

  const dirty = (secId) => values[secId].job_id !== initial[secId].job_id
    || values[secId].note !== initial[secId].note
    || values[secId].rag !== initial[secId].rag
    || values[secId].shift_start !== initial[secId].shift_start
    || values[secId].shift_end !== initial[secId].shift_end
    || values[secId].linked_person_id !== initial[secId].linked_person_id
    || values[secId].standby_level_id !== initial[secId].standby_level_id;
  const anyDirty = editableSections.some(s => dirty(s.id));
  // Say what Save is about to write, so a stray tick is caught before it lands
  // on four other people rather than after.
  const saveLabel = copyTo.size && extraDays.size ? `Save (${copyTo.size + 1} rows, ${extraDays.size + 1} days)`
    : copyTo.size ? `Save (${copyTo.size + 1} rows)`
    : extraDays.size ? `Save (${extraDays.size + 1} days)`
    : 'Save';

  const save = async () => {
    setError('');
    setSaving(true);
    const dates = [date, ...[...extraDays]];
    // The row being edited first, then anybody this day is being copied out
    // to.
    const targets = [rowId, ...[...copyTo].filter(id => id !== rowId)];
    // A copy is several people's cells written one after another, so a refusal
    // partway through (a standby clash, say) has to name whose it was.
    let onBehalfOf = null;
    try {
      for (const target of targets) {
        const isSource = target === rowId;
        const targetMember = data.members.find(m => rowKey(m) === target);
        onBehalfOf = isSource ? null : personLabel(targetMember || {});
        for (const sec of editableSections) {
          // The row being edited only takes what actually changed, unless the
          // day chips are repainting it. A copy target takes the whole day,
          // because it has none of it yet.
          if (isSource && !dirty(sec.id) && !extraDays.size) continue;
          // With extra days or extra people, every touched-or-set section is
          // painted across them; untouched EMPTY sections are left alone so
          // painting an AM job does not blank the other days' PM, or somebody
          // else's.
          if (!dirty(sec.id) && !values[sec.id].job_id && !values[sec.id].note && !values[sec.id].linked_person_id && !values[sec.id].standby_level_id) continue;
          const isShift = sec.kind === 'shift_time';
          await api.put(`plows/${data.plow.id}/slots`, {
            person_id: target,
            section_id: sec.id,
            dates,
            job_id: sec.kind === 'assignment' ? (values[sec.id].job_id || null) : null,
            note: values[sec.id].note || null,
            rag: sec.kind === 'assignment' ? (values[sec.id].rag || null) : null,
            shift_start: isShift ? (values[sec.id].shift_start || null) : null,
            shift_end: isShift ? (values[sec.id].shift_end || null) : null,
            linked_person_id: sec.kind === 'person' ? (values[sec.id].linked_person_id || null) : null,
            standby_level_id: sec.kind === 'standby' ? (values[sec.id].standby_level_id || null) : null,
          });
        }
      }
      if (copyTo.size) toast.success(`Copied to ${copyTo.size} other ${copyTo.size === 1 ? 'row' : 'rows'}`);
      onSaved();
    } catch (e) {
      const why = e.message || 'The assignment could not be saved';
      setError(onBehalfOf ? `Copying to ${onBehalfOf} stopped here: ${why}` : why);
      setSaving(false);
    }
  };

  const clearDay = async () => {
    setError('');
    setSaving(true);
    try {
      for (const sec of editableSections) {
        await api.put(`plows/${data.plow.id}/slots`, {
          person_id: rowId, section_id: sec.id, dates: [date], job_id: null, note: null,
        });
      }
      toast.success('Cell cleared');
      onSaved();
    } catch (e) {
      setError(e.message || 'The cell could not be cleared');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`${personLabel(member || {})} · ${fmtDay(date)} ${fmtShort(date)}`}
      onClose={onClose}
      footer={<>
        <div className="mr-auto flex items-center gap-1 flex-wrap">
          <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-red-600 hover:text-red-700" onClick={clearDay} disabled={saving}>
            Clear this day
          </button>
          {/* A secondary action, so it stays the size of the one beside it:
              the picker only appears once asked for. */}
          {copyCandidates.length > 0 && !copyPickerOpen && (
            <button
              type="button"
              className="px-3 py-1.5 text-[10px] font-semibold text-navy hover:underline"
              onClick={() => { setError(''); setCopyPickerOpen(true); }}
              disabled={saving}
              title="Fill this day in from another person's same day, then adjust and save"
              data-tour="plow-copy-from"
            >
              {copiedFrom ? `Copied from ${personLabel(copiedFrom)}` : 'Copy from…'}
            </button>
          )}
          {copyPickerOpen && (
            <select
              className="form-select grow-0 shrink-0 basis-44"
              value=""
              autoFocus
              onChange={e => copyFrom(e.target.value)}
              onBlur={() => setCopyPickerOpen(false)}
              aria-label="Copy another person's day"
            >
              <option value="">Copy from…</option>
              {copyCandidates.map(m => <option key={rowKey(m)} value={rowKey(m)}>{personLabel(m)}</option>)}
            </select>
          )}
          {/* The same size and place as Copy from, because it is the same idea
              pointed the other way. The list opens over the footer rather than
              growing it, so the dialog does not jump. */}
          {copyCandidates.length > 0 && (
            <div className="relative">
              <button
                type="button"
                className="px-3 py-1.5 text-[10px] font-semibold text-navy hover:underline"
                onClick={() => setCopyToOpen(o => !o)}
                disabled={saving}
                title="Give this same day to other people, saved with this one"
                aria-expanded={copyToOpen}
                data-tour="plow-copy-to"
              >
                {copyTo.size ? `Copy to ${copyTo.size} other${copyTo.size === 1 ? '' : 's'}` : 'Copy to…'}
              </button>
              {copyToOpen && (
                <div className="absolute bottom-full left-0 mb-1 z-10 w-60 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl p-2">
                  <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wide px-1 pb-1">Copy this day to</p>
                  {copyCandidates.map(m => (
                    <label key={rowKey(m)} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        className="shrink-0"
                        checked={copyTo.has(rowKey(m))}
                        onChange={() => toggleCopyTo(rowKey(m))}
                      />
                      <span className="text-[11px] text-gray-700 truncate">{personLabel(m)}</span>
                    </label>
                  ))}
                  <p className="text-[9px] text-gray-400 mt-1 px-1">
                    Each ticked row gets this day as it stands above, on Save.
                    {paySections.length > 0 && ' The shift time and standby come with it, so each person raises their own pending claim.'}
                  </p>
                  <div className="flex justify-between items-center gap-2 mt-1 px-1">
                    <button type="button" className="text-[9px] text-gray-500 hover:text-gray-700 disabled:opacity-40" onClick={() => setCopyTo(new Set())} disabled={!copyTo.size}>
                      Clear
                    </button>
                    <button type="button" className="text-[9px] font-bold text-navy hover:underline" onClick={() => setCopyToOpen(false)}>Done</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="px-4 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50"
          onClick={save}
          disabled={saving || (!anyDirty && !copyTo.size)}
        >
          {saving ? 'Saving…' : saveLabel}
        </button>
      </>}
    >
      <div className="space-y-3">
        {editableSections.map(sec => {
          // A past cell may still hold a job that has since been archived.
          // It is kept out of the list for new picks, but the current value
          // has to be an option or the select shows "(empty)" over an
          // assignment the grid plainly displays.
          const currentArchived = data.jobs.find(j => j.id === values[sec.id].job_id && !j.active);
          return (
          <div key={sec.id}>
            <label className="form-label">{sec.label}</label>
            {sec.kind === 'text' ? (
              <input
                className="form-input"
                value={values[sec.id].note}
                onChange={e => setVal(sec.id, { note: e.target.value })}
                placeholder="Anything: a time, a place, a name"
              />
            ) : sec.kind === 'person' ? (
              <select
                className="form-select"
                value={values[sec.id].linked_person_id}
                onChange={e => setVal(sec.id, { linked_person_id: e.target.value })}
              >
                <option value="">(nobody)</option>
                {(people || []).map(p => (
                  <option key={p.id} value={p.id}>{`${p.first_name || ''} ${p.last_name || ''}`.trim()}</option>
                ))}
              </select>
            ) : sec.kind === 'standby' ? (
              <div>
                <div className="flex gap-2 items-center">
                  <select
                    className="form-select flex-1"
                    value={values[sec.id].standby_level_id}
                    onChange={e => setVal(sec.id, { standby_level_id: e.target.value })}
                  >
                    <option value="">(not on standby)</option>
                    {(data.standby_levels || []).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  <input
                    className="form-input grow-0 shrink-0 basis-40"
                    value={values[sec.id].note}
                    onChange={e => setVal(sec.id, { note: e.target.value })}
                    placeholder="Note"
                  />
                </div>
                {/* Standby has no clock times: the policy fixes the window, so the
                    date says the hours. Shown here so nobody has to know the rule. */}
                {(() => {
                  const slot = standbySlotFor(date);
                  if (!slot) return null;
                  return values[sec.id].standby_level_id ? (
                    <div className="mt-1 text-[9px] text-gray-500">
                      {slot.label}, {slot.window}: {slot.hours}h at £{STANDBY_RATE_PER_HOUR}/h, £{standbyPay(slot.hours)}. Raised as pending standby; approved when the week is confirmed. A bank holiday counts as a weekend day.
                    </div>
                  ) : (
                    <div className="mt-1 text-[9px] text-gray-400">Pick a level to put {personLabel(member || {})} on standby: {slot.label.toLowerCase()} here is {slot.hours}h.</div>
                  );
                })()}
              </div>
            ) : sec.kind === 'shift_time' ? (
              <div>
                <div className="flex gap-2 items-center">
                  <input type="time" className="form-input grow-0 shrink-0 basis-28" step="900"
                    value={values[sec.id].shift_start}
                    onChange={e => setVal(sec.id, { shift_start: e.target.value })} />
                  <span className="text-[10px] text-gray-500">to</span>
                  <input type="time" className="form-input grow-0 shrink-0 basis-28" step="900"
                    value={values[sec.id].shift_end}
                    onChange={e => setVal(sec.id, { shift_end: e.target.value })} />
                  <input
                    className="form-input flex-1"
                    value={values[sec.id].note}
                    onChange={e => setVal(sec.id, { note: e.target.value })}
                    placeholder="Note"
                  />
                </div>
                {/* The uplift the planner is committing to, shown as they set it,
                    rather than found by payroll a month later. */}
                {isShiftMove(values[sec.id].shift_start, values[sec.id].shift_end) ? (
                  <div className="mt-1 text-[9px] font-semibold text-amber-700">
                    Outside 7am to 6pm: a shift move, paid at the hourly rate plus 25%. It will show on the Overtime page for approval.
                  </div>
                ) : (
                  <div className="mt-1 text-[9px] text-gray-400">Within usual hours (7am to 6pm), so no uplift.</div>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  className="form-select flex-1"
                  value={values[sec.id].job_id}
                  onChange={e => setVal(sec.id, { job_id: e.target.value })}
                >
                  <option value="">(empty)</option>
                  <optgroup label="Statuses">
                    {statuses.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                  </optgroup>
                  <optgroup label="Jobs">
                    {workJobs.map(j => <option key={j.id} value={j.id}>{j.name}{j.sow_reference ? ` (${j.sow_reference})` : ''}</option>)}
                    {currentArchived && (
                      <option value={currentArchived.id}>
                        {currentArchived.name}{currentArchived.sow_reference ? ` (${currentArchived.sow_reference})` : ''} (archived)
                      </option>
                    )}
                  </optgroup>
                </select>
                {data.plow.can_edit && (
                  <button
                    type="button"
                    className="px-2 py-1 text-[9px] font-bold text-navy border border-navy rounded hover:bg-blue-50 shrink-0 whitespace-nowrap"
                    onClick={() => openNewJob(sec.id)}
                    title="Create a job in the library and assign it here"
                    aria-expanded={newJobFor === sec.id}
                  >
                    + New job
                  </button>
                )}
                <input
                  className="form-input grow-0 shrink-0 basis-32"
                  value={values[sec.id].note}
                  onChange={e => setVal(sec.id, { note: e.target.value })}
                  placeholder="Note"
                />
                {/* The traffic light: click a dot to flag the booking, click
                    it again to clear. */}
                <div className="flex items-center gap-1.5 shrink-0" role="group" aria-label={`${sec.label} traffic light`}>
                  {Object.entries(RAG).map(([key, r]) => (
                    <button
                      key={key}
                      type="button"
                      aria-label={r.label}
                      title={r.label}
                      aria-pressed={values[sec.id].rag === key}
                      onClick={() => setVal(sec.id, { rag: values[sec.id].rag === key ? '' : key })}
                      className={`w-4 h-4 rounded-full transition-all ${r.dot}
                        ${values[sec.id].rag === key ? 'ring-2 ring-blue-500 ring-offset-1' : 'opacity-25 hover:opacity-70'}`}
                    />
                  ))}
                </div>
              </div>
            )}
            {newJobFor === sec.id && (
              <div className="mt-1.5 bg-gray-50 rounded-lg p-2 space-y-1.5" data-tour="plow-new-job">
                <div className="flex gap-2 items-center">
                  <input
                    className="form-input flex-1"
                    value={newJob.name}
                    onChange={e => setNewJob(v => ({ ...v, name: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createJob(); } }}
                    placeholder="Job name"
                    autoFocus
                  />
                  <select className="form-select grow-0 shrink-0 basis-24" value={newJob.tone} onChange={e => setNewJob(v => ({ ...v, tone: e.target.value }))}>
                    {TONE_KEYS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input
                    className="form-input grow-0 shrink-0 basis-32"
                    value={newJob.sowRef}
                    onChange={e => setNewJob(v => ({ ...v, sowRef: e.target.value }))}
                    onBlur={prefillNewJobName}
                    placeholder="AV-… (optional)"
                  />
                  <button type="button" className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50" onClick={createJob} disabled={creating}>
                    {creating ? 'Adding…' : 'Add'}
                  </button>
                  <button type="button" className="text-[10px] text-gray-500 hover:text-gray-700" onClick={() => setNewJobFor(null)}>Cancel</button>
                </div>
                <p className="text-[9px] text-gray-400">Added to this board's job library and selected for {sec.label}.</p>
              </div>
            )}
          </div>
          );
        })}

        <div>
          <label className="form-label">Also apply to</label>
          <div className="flex flex-wrap gap-1.5">
            {data.dates.filter(d => d !== date).map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setExtraDays(prev => {
                  const next = new Set(prev);
                  if (next.has(d)) next.delete(d); else next.add(d);
                  return next;
                })}
                className={`px-2 py-1 rounded text-[9px] font-semibold border transition-colors
                  ${extraDays.has(d) ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-navy'}`}
              >
                {fmtDay(d)} {fmtShort(d)}
              </button>
            ))}
          </div>
          <p className="text-[9px] text-gray-400 mt-1">Ticked days get the same assignment for the sections set above.</p>
        </div>

        {error && <p className="text-[10px] text-red-600 font-semibold">{error}</p>}
      </div>
    </Modal>
  );
}

// ── The label row editor: one lane, one day, as many tasks as there are ───────
// A label row's day is a list, not a cell per section: add a task, take one
// away, move one up or down, and the list as it stands is what Save writes.
// Each task is a job or a note with its own traffic light. "Also apply to"
// gives other days the same list, whole.
const blankTask = () => ({ key: `new-${Math.random().toString(36).slice(2)}`, job_id: '', note: '', rag: '' });
function LabelTasksEditorModal({ data, rowId, date, onClose, onSaved, onRefresh }) {
  const member = data.members.find(m => rowKey(m) === rowId);
  const statuses = data.jobs.filter(j => j.kind === 'status' && j.active);
  const workJobs = data.jobs.filter(j => j.kind === 'work' && j.active);
  const initial = useMemo(() => (data.label_tasks || [])
    .filter(t => t.member_id === rowId && t.task_date === date)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(t => ({ key: t.id, job_id: t.job_id || '', note: t.note || '', rag: t.rag || '' })), [data, rowId, date]);
  // An empty day opens with one blank line, so the first task is a pick away
  // rather than a click and then a pick.
  const [tasks, setTasks] = useState(() => (initial.length ? initial : [blankTask()]));
  const [extraDays, setExtraDays] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newJobFor, setNewJobFor] = useState(null);
  const [newJob, setNewJob] = useState({ name: '', tone: 'blue', sowRef: '' });
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const setTask = (key, patch) => setTasks(list => list.map(t => (t.key === key ? { ...t, ...patch } : t)));
  const removeTask = (key) => setTasks(list => list.filter(t => t.key !== key));
  const moveTask = (key, dir) => setTasks(list => {
    const i = list.findIndex(t => t.key === key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const addTask = () => setTasks(list => [...list, blankTask()]);

  // A line with nothing on it is not a task: it is left out of the save
  // rather than refused, so an unused blank line never blocks Save.
  const filled = tasks.filter(t => t.job_id || t.note.trim());
  const same = (a, b) => a.length === b.length && a.every((t, i) => t.job_id === b[i].job_id && t.note.trim() === b[i].note.trim() && t.rag === b[i].rag);
  const dirty = !same(filled, initial);
  const saveLabel = extraDays.size ? `Save (${extraDays.size + 1} days)` : 'Save';

  const openNewJob = (key) => {
    setError('');
    setNewJob({ name: '', tone: 'blue', sowRef: '' });
    setNewJobFor(prev => (prev === key ? null : key));
  };
  const prefillNewJobName = async () => {
    if (newJob.name.trim()) return;
    const name = await suggestJobName(newJob.sowRef);
    if (name) setNewJob(v => (v.name.trim() ? v : { ...v, name }));
  };
  const createJob = async () => {
    setError('');
    if (!newJob.name.trim()) { setError('Give the job a name'); return; }
    setCreating(true);
    try {
      const created = await api.post(`plows/${data.plow.id}/jobs`, {
        name: newJob.name.trim(), tone: newJob.tone, sow_reference: newJob.sowRef.trim(),
      });
      if (onRefresh) await onRefresh();
      setTask(newJobFor, { job_id: created.id });
      setNewJobFor(null);
      toast.success(`"${newJob.name.trim()}" added to the job library`);
    } catch (e) {
      setError(e.message || 'The job could not be created');
    }
    setCreating(false);
  };

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      await api.put(`plows/${data.plow.id}/label-tasks`, {
        member_id: rowId,
        dates: [date, ...[...extraDays]],
        tasks: filled.map(t => ({ job_id: t.job_id || null, note: t.note.trim() || null, rag: t.rag || null })),
      });
      onSaved();
    } catch (e) {
      setError(e.message || 'The tasks could not be saved');
      setSaving(false);
    }
  };
  const clearDay = async () => {
    setError('');
    setSaving(true);
    try {
      await api.put(`plows/${data.plow.id}/label-tasks`, { member_id: rowId, dates: [date], tasks: [] });
      toast.success('Day cleared');
      onSaved();
    } catch (e) {
      setError(e.message || 'The day could not be cleared');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`${personLabel(member || {})} · ${fmtDay(date)} ${fmtShort(date)}`}
      onClose={onClose}
      footer={<>
        <div className="mr-auto">
          <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-red-600 hover:text-red-700" onClick={clearDay} disabled={saving || !initial.length}>
            Clear this day
          </button>
        </div>
        <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="px-4 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50"
          onClick={save}
          disabled={saving || (!dirty && !extraDays.size)}
        >
          {saving ? 'Saving…' : saveLabel}
        </button>
      </>}
    >
      <div className="space-y-3">
        <div>
          <label className="form-label">Tasks, in order</label>
          <div className="space-y-1.5">
            {tasks.map((t, i) => {
              const currentArchived = data.jobs.find(j => j.id === t.job_id && !j.active);
              return (
                <div key={t.key}>
                  <div className="flex gap-2 items-center">
                    <span className="text-[9px] font-bold text-gray-400 w-4 shrink-0 text-right">{i + 1}</span>
                    <select
                      className="form-select flex-1"
                      value={t.job_id}
                      onChange={e => setTask(t.key, { job_id: e.target.value })}
                      aria-label={`Task ${i + 1} job`}
                    >
                      <option value="">(note only)</option>
                      <optgroup label="Statuses">
                        {statuses.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                      </optgroup>
                      <optgroup label="Jobs">
                        {workJobs.map(j => <option key={j.id} value={j.id}>{j.name}{j.sow_reference ? ` (${j.sow_reference})` : ''}</option>)}
                        {currentArchived && (
                          <option value={currentArchived.id}>
                            {currentArchived.name}{currentArchived.sow_reference ? ` (${currentArchived.sow_reference})` : ''} (archived)
                          </option>
                        )}
                      </optgroup>
                    </select>
                    {data.plow.can_edit && (
                      <button
                        type="button"
                        className="px-2 py-1 text-[9px] font-bold text-navy border border-navy rounded hover:bg-blue-50 shrink-0 whitespace-nowrap"
                        onClick={() => openNewJob(t.key)}
                        title="Create a job in the library and pick it here"
                        aria-expanded={newJobFor === t.key}
                      >
                        + New job
                      </button>
                    )}
                    <input
                      className="form-input grow-0 shrink-0 basis-32"
                      value={t.note}
                      onChange={e => setTask(t.key, { note: e.target.value })}
                      placeholder="Note"
                      aria-label={`Task ${i + 1} note`}
                    />
                    <div className="flex items-center gap-1.5 shrink-0" role="group" aria-label={`Task ${i + 1} traffic light`}>
                      {Object.entries(RAG).map(([key, r]) => (
                        <button
                          key={key}
                          type="button"
                          aria-label={r.label}
                          title={r.label}
                          aria-pressed={t.rag === key}
                          onClick={() => setTask(t.key, { rag: t.rag === key ? '' : key })}
                          className={`w-4 h-4 rounded-full transition-all ${r.dot}
                            ${t.rag === key ? 'ring-2 ring-blue-500 ring-offset-1' : 'opacity-25 hover:opacity-70'}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center shrink-0">
                      <button type="button" className="text-[10px] text-gray-400 hover:text-navy px-1 disabled:opacity-30" onClick={() => moveTask(t.key, -1)} disabled={i === 0} aria-label={`Move task ${i + 1} up`}>↑</button>
                      <button type="button" className="text-[10px] text-gray-400 hover:text-navy px-1 disabled:opacity-30" onClick={() => moveTask(t.key, 1)} disabled={i === tasks.length - 1} aria-label={`Move task ${i + 1} down`}>↓</button>
                      <button type="button" className="text-[10px] text-gray-400 hover:text-red-600 px-1" onClick={() => removeTask(t.key)} aria-label={`Remove task ${i + 1}`}>✕</button>
                    </div>
                  </div>
                  {newJobFor === t.key && (
                    <div className="mt-1.5 ml-6 bg-gray-50 rounded-lg p-2 space-y-1.5">
                      <div className="flex gap-2 items-center">
                        <input
                          className="form-input flex-1"
                          value={newJob.name}
                          onChange={e => setNewJob(v => ({ ...v, name: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createJob(); } }}
                          placeholder="Job name"
                          autoFocus
                        />
                        <select className="form-select grow-0 shrink-0 basis-24" value={newJob.tone} onChange={e => setNewJob(v => ({ ...v, tone: e.target.value }))}>
                          {TONE_KEYS.map(tone => <option key={tone} value={tone}>{tone}</option>)}
                        </select>
                        <input
                          className="form-input grow-0 shrink-0 basis-32"
                          value={newJob.sowRef}
                          onChange={e => setNewJob(v => ({ ...v, sowRef: e.target.value }))}
                          onBlur={prefillNewJobName}
                          placeholder="AV-… (optional)"
                        />
                        <button type="button" className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50" onClick={createJob} disabled={creating}>
                          {creating ? 'Adding…' : 'Add'}
                        </button>
                        <button type="button" className="text-[10px] text-gray-500 hover:text-gray-700" onClick={() => setNewJobFor(null)}>Cancel</button>
                      </div>
                      <p className="text-[9px] text-gray-400">Added to this board's job library and picked for task {i + 1}.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className="mt-2 px-2.5 py-1 text-[10px] font-bold text-navy border border-navy rounded hover:bg-blue-50"
            onClick={addTask}
          >
            + Add task
          </button>
          <p className="text-[9px] text-gray-400 mt-1">
            {personLabel(member || {})} is not a person, so its day is a list rather than {data.sections.filter(sec => sec.kind === 'assignment').map(sec => sec.label).join(' and ') || 'sections'}: add as many tasks as there are. A line with nothing on it is not saved.
          </p>
        </div>

        <div>
          <label className="form-label">Also apply to</label>
          <div className="flex flex-wrap gap-1.5">
            {data.dates.filter(d => d !== date).map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setExtraDays(prev => {
                  const next = new Set(prev);
                  if (next.has(d)) next.delete(d); else next.add(d);
                  return next;
                })}
                className={`px-2 py-1 rounded text-[9px] font-semibold border transition-colors
                  ${extraDays.has(d) ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-navy'}`}
              >
                {fmtDay(d)} {fmtShort(d)}
              </button>
            ))}
          </div>
          <p className="text-[9px] text-gray-400 mt-1">Ticked days get this whole list in place of whatever they hold.</p>
        </div>

        {error && <p className="text-[10px] text-red-600 font-semibold">{error}</p>}
      </div>
    </Modal>
  );
}

// Module-level for the same remount reason as PersonAdder.
// ── The read-only cell: one person, one day, everything the grid truncates ────
// A viewer or member cannot open the editor, and the grid squeezes every chip
// to one line with the SOW reference, note and traffic light behind a hover
// title a phone never shows. So their tap opens this instead: the same cell,
// every section by its label, written out in full. It saves nothing.
function CellDetailModal({ data, rowId, date, jobById, absence, onClose }) {
  const member = data.members.find(m => rowKey(m) === rowId);
  // A label row has no person, so no allowance was ever raised against it,
  // and no sections: its day is the task list, shown in order below.
  const personId = member?.person_id || null;
  const isLabel = isLabelRow(member);
  const sections = isLabel ? [] : data.sections;
  const labelTasks = isLabel
    ? (data.label_tasks || []).filter(t => t.member_id === rowId && t.task_date === date).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    : [];
  const deskMove = (data.desk_move_allowances || []).find(a => a.person_id === personId && a.date === date) || null;
  const workingAway = (data.working_away_records || []).find(a => a.person_id === personId && a.date === date) || null;
  const covers = (sec) => !!absence && (absence.slot === 'full' || String(sec.label || '').trim().toLowerCase() === absence.slot);
  const Row = ({ label, children }) => (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 last:border-b-0">
      <span className="text-[11px] text-gray-400 shrink-0 w-24">{label}</span>
      <span className="text-[12px] text-gray-800 text-right min-w-0 break-words">{children}</span>
    </div>
  );
  const standbyStatus = (st) => st === 'approved' ? 'confirmed' : st === 'pending' ? 'pending' : st || '';
  return (
    <Modal
      title={`${personLabel(member || {})} · ${fmtDay(date)} ${fmtShort(date)}`}
      onClose={onClose}
      footer={<button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Close</button>}
    >
      {absence && (
        <div className={`mb-3 rounded px-3 py-2 text-[12px] font-semibold ${ABSENCE_TONES[absence.kind] || ABSENCE_TONES.leave}`}>
          {absence.label}{absence.slot !== 'full' ? ` (${absence.slot.toUpperCase()})` : ''}
        </div>
      )}
      <div className="space-y-4">
        {isLabel && (
          <div>
            <div className="text-[9px] font-bold uppercase tracking-wide text-gray-400 mb-1">Tasks</div>
            {labelTasks.length ? labelTasks.map((t, i) => {
              const job = t.job_id ? jobById[t.job_id] : null;
              return (
                <div key={t.id} className="flex items-start gap-3 py-1.5 border-b border-gray-100 last:border-b-0">
                  <span className="text-[11px] text-gray-400 shrink-0 w-4 text-right">{i + 1}</span>
                  <div className="min-w-0 text-[12px] text-gray-800">
                    {job && <span className={`inline-block rounded px-1.5 py-0.5 font-semibold ${TONES[job.tone] || TONES.blue}`}>{job.name}</span>}
                    {job?.sow_reference && <span className="ml-1.5 text-gray-500">{job.sow_reference}</span>}
                    {t.note && <div className={job ? 'text-gray-600 mt-0.5' : ''}>{t.note}</div>}
                    {t.rag && (
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        <span className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${RAG[t.rag]?.dot || ''}`} />{RAG[t.rag]?.label}
                      </div>
                    )}
                  </div>
                </div>
              );
            }) : <div className="text-[12px] text-gray-400 italic py-1">Nothing planned</div>}
          </div>
        )}
        {sections.map(sec => {
          const s = data.slots.find(x => slotRowKey(x) === rowId && x.slot_date === date && x.section_id === sec.id);
          const job = s?.job_id ? jobById[s.job_id] : null;
          const covered = covers(sec);
          let rows = [];
          if (sec.kind === 'text') {
            rows = s?.note ? [<Row key="note" label="Note">{s.note}</Row>] : [];
          } else if (sec.kind === 'person') {
            rows = s?.linked_person_name ? [<Row key="who" label="Who">{s.linked_person_name}</Row>] : [];
          } else if (sec.kind === 'standby') {
            if (s?.standby_level_id) {
              rows = [
                <Row key="level" label="Level">{s.standby_level_name || 'Standby'}</Row>,
                s.standby_status && <Row key="status" label="Status">{standbyStatus(s.standby_status)}</Row>,
                s.standby_hours != null && <Row key="hours" label="Hours">{s.standby_hours}h</Row>,
              ];
            }
            if (s?.note) rows.push(<Row key="note" label="Note">{s.note}</Row>);
          } else if (sec.kind === 'shift_time') {
            if (s?.shift_start) {
              const moved = isShiftMove(s.shift_start, s.shift_end);
              rows = [
                <Row key="time" label="Time">{s.shift_start} to {s.shift_end}</Row>,
                <Row key="moved" label="Hours">{moved ? 'Outside 7am to 6pm: a shift move at +25%' : 'Within usual hours'}</Row>,
              ];
            } else {
              rows = [<Row key="time" label="Time">Standard shift</Row>];
            }
            if (s?.note) rows.push(<Row key="note" label="Note">{s.note}</Row>);
          } else {
            if (job) {
              rows = [
                <Row key="job" label="Job">
                  <span className={`inline-block rounded px-1.5 py-0.5 font-semibold ${TONES[job.tone] || TONES.blue} ${covered ? 'line-through opacity-60' : ''}`}>{job.name}</span>
                </Row>,
                job.sow_reference && <Row key="sow" label="SOW">{job.sow_reference}</Row>,
                s.note && <Row key="note" label="Note">{s.note}</Row>,
                s.rag && (
                  <Row key="rag" label="Status">
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${RAG[s.rag]?.dot || ''}`} />{RAG[s.rag]?.label}
                  </Row>
                ),
                !!job.is_desk_move && deskMove && (
                  <Row key="move" label="Desk move">
                    £{deskMove.request_type === 'desk_move_weekend' ? 20 : 15} allowance, {deskMove.status === 'approved' ? 'approved' : 'awaiting the line manager'}
                  </Row>
                ),
                !!job.is_working_away && workingAway && (
                  <Row key="away" label="Working away">
                    +25% on a {workingAway.hours}h shift, {workingAway.status === 'approved' ? 'approved' : 'pending'}
                  </Row>
                ),
                covered && <Row key="cover" label="Cover">{absence.kind === 'blocked' ? 'Assigned on a blocked day' : `Assigned before ${absence.label.toLowerCase()}`}; needs reassigning</Row>,
              ];
            } else if (s?.note) {
              rows = [<Row key="note" label="Note">{s.note}</Row>];
            }
          }
          rows = rows.filter(Boolean);
          return (
            <div key={sec.id}>
              <div className="text-[9px] font-bold uppercase tracking-wide text-gray-400 mb-1">{sec.label || sec.kind}</div>
              {rows.length ? rows : <div className="text-[12px] text-gray-400 italic py-1">Nothing planned</div>}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function JobRow({ j, editing, edit, setEdit, busy, onStartEdit, onCancelEdit, onSave, onRemove, onArchive, onRestore }) {
  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 ${j.active ? '' : 'opacity-50'}`}>
      {editing ? (
        <>
          {j.is_builtin
            ? <span className="text-[10px] font-semibold text-gray-700 flex-1">{j.name}</span>
            : <input className="form-input flex-1" value={edit.name} onChange={e => setEdit(v => ({ ...v, name: e.target.value }))} />}
          <select className="form-select grow-0 shrink-0 basis-24" value={edit.tone} onChange={e => setEdit(v => ({ ...v, tone: e.target.value }))}>
            {TONE_KEYS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {!j.is_builtin && (
            <input className="form-input grow-0 shrink-0 basis-32" list="plow-sow-refs" value={edit.sowRef}
              onChange={e => setEdit(v => ({ ...v, sowRef: e.target.value }))} placeholder="AV-… (optional)" />
          )}
          {!j.is_builtin && (
            <label className="flex items-center gap-1 text-[9px] text-gray-600 whitespace-nowrap" title="Assigning this job raises the £15 weekday or £20 weekend desk move allowance for that person and day">
              <input type="checkbox" className="accent-navy" checked={!!edit.isDeskMove} onChange={e => setEdit(v => ({ ...v, isDeskMove: e.target.checked }))} />
              Desk move or install
            </label>
          )}
          {!j.is_builtin && (
            <label className="flex items-center gap-1 text-[9px] text-gray-600 whitespace-nowrap" title="Assigning this job pays a quarter of the hourly rate on that day's shift for working away (Overtime Policy 2.a.vi)">
              <input type="checkbox" className="accent-navy" checked={!!edit.isWorkingAway} onChange={e => setEdit(v => ({ ...v, isWorkingAway: e.target.checked }))} />
              Working away
            </label>
          )}
          <button type="button" className="text-[10px] font-bold text-navy" onClick={onSave} disabled={busy}>Save</button>
          <button type="button" className="text-[10px] text-gray-500" onClick={onCancelEdit}>Cancel</button>
        </>
      ) : (
        <>
          <span className={`w-3 h-3 rounded-full shrink-0 ${TONE_DOTS[j.tone] || TONE_DOTS.blue}`} />
          <span className="text-[10px] font-semibold text-gray-700 flex-1 truncate">
            {j.name}
            {j.sow_reference && <span className="ml-1.5 font-normal text-gray-400">{j.sow_reference}</span>}
            {!!j.is_desk_move && <span className="ml-1.5 text-[7px] font-bold uppercase bg-amber-100 text-amber-800 rounded px-1" title="Raises the desk move allowance when assigned">move</span>}
            {!!j.is_working_away && <span className="ml-1.5 text-[7px] font-bold uppercase bg-sky-100 text-sky-800 rounded px-1" title="Pays the working away uplift (+25%) when assigned">away</span>}
            {!j.active && <span className="ml-1.5 font-normal text-gray-400">(archived)</span>}
          </span>
          <button type="button" className="text-[10px] text-gray-500 hover:text-navy" onClick={onStartEdit}>Edit</button>
          {/* Archive takes a job out of the cell editor's selector while every
              past cell keeps it, so a finished SOW's history stays legible.
              Delete is for a job that should never have existed; the server
              archives it anyway if it is on any week. */}
          {!j.is_builtin && !!j.active && (
            <button type="button" className="text-[10px] text-gray-500 hover:text-navy" onClick={onArchive} disabled={busy} title="Hide from the job selector; past weeks keep it">
              Archive
            </button>
          )}
          {!j.is_builtin && !j.active && (
            <button type="button" className="text-[10px] text-gray-500 hover:text-navy" onClick={onRestore} disabled={busy} title="Put back in the job selector">
              Restore
            </button>
          )}
          {!j.is_builtin && (
            <button type="button" className="text-[10px] text-red-500 hover:text-red-700" onClick={onRemove} disabled={busy}>Delete</button>
          )}
        </>
      )}
    </div>
  );
}

// ── The job library ───────────────────────────────────────────────────────────
function JobsModal({ plowId, jobs, onClose, onChanged }) {
  const { toast } = useToast();
  const [sows, setSows] = useState([]);
  const [addName, setAddName] = useState('');
  const [addTone, setAddTone] = useState('blue');
  const [addSowRef, setAddSowRef] = useState('');
  const [addMove, setAddMove] = useState(false);
  const [addAway, setAddAway] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Live SOWs, as autocomplete hints only. The typed reference is sent to the
  // server as sow_reference and resolved there against every SOW, so a
  // reference outside this list still links: the list used to be the lookup,
  // capped at the 500 most recently updated, and an active SOW beyond the cap
  // was refused as not existing.
  useEffect(() => {
    api.get('sows', { status: 'Active' })
      .then(r => setSows(Array.isArray(r) ? r : (r?.entries || [])))
      .catch(() => setSows([]));
  }, []);

  const prefillAddName = async () => {
    if (addName.trim()) return;
    const name = await suggestJobName(addSowRef);
    if (name) setAddName(prev => (prev.trim() ? prev : name));
  };

  const addJob = async () => {
    setError('');
    if (!addName.trim()) { setError('Give the job a name'); return; }
    setBusy(true);
    try {
      await api.post(`plows/${plowId}/jobs`, { name: addName.trim(), tone: addTone, sow_reference: addSowRef.trim(), is_desk_move: addMove, is_working_away: addAway });
      setAddName(''); setAddSowRef(''); setAddMove(false); setAddAway(false);
      onChanged();
    } catch (e) { setError(e.message || 'The job could not be added'); }
    setBusy(false);
  };

  const startEdit = (j) => {
    setEditingId(j.id);
    setEdit({ name: j.name, tone: j.tone, sowRef: j.sow_reference || '', isDeskMove: !!j.is_desk_move, isWorkingAway: !!j.is_working_away });
    setError('');
  };

  const saveEdit = async (j) => {
    setError('');
    setBusy(true);
    try {
      await api.put(`plows/${plowId}/jobs/${j.id}`, j.is_builtin
        ? { tone: edit.tone }
        : { name: edit.name.trim() || j.name, tone: edit.tone, sow_reference: edit.sowRef.trim(), is_desk_move: edit.isDeskMove, is_working_away: edit.isWorkingAway });
      setEditingId(null);
      onChanged();
    } catch (e) { setError(e.message || 'The job could not be saved'); }
    setBusy(false);
  };

  const setArchived = async (j, archived) => {
    setError('');
    setBusy(true);
    try {
      await api.put(`plows/${plowId}/jobs/${j.id}`, { active: !archived });
      toast.success(archived
        ? `"${j.name}" archived: it is out of the job selector and past weeks keep it`
        : `"${j.name}" restored to the job selector`);
      onChanged();
    } catch (e) { setError(e.message || (archived ? 'The job could not be archived' : 'The job could not be restored')); }
    setBusy(false);
  };

  const removeJob = async (j) => {
    setError('');
    setBusy(true);
    try {
      const r = await api.delete(`plows/${plowId}/jobs/${j.id}`);
      toast.success(r?.retired ? 'The job is on past weeks, so it was archived rather than deleted' : 'Job deleted');
      onChanged();
    } catch (e) { setError(e.message || 'The job could not be deleted'); }
    setBusy(false);
  };

  const Row = ({ j }) => {
    const editing = editingId === j.id;
    return (
      <JobRow
        j={j}
        editing={editing}
        edit={edit}
        setEdit={setEdit}
        busy={busy}
        onStartEdit={() => startEdit(j)}
        onCancelEdit={() => setEditingId(null)}
        onSave={() => saveEdit(j)}
        onRemove={() => removeJob(j)}
        onArchive={() => setArchived(j, true)}
        onRestore={() => setArchived(j, false)}
      />
    );
  };

  const statuses = jobs.filter(j => j.kind === 'status');
  const work = jobs.filter(j => j.kind === 'work' && j.active);
  const archived = jobs.filter(j => j.kind === 'work' && !j.active);
  return (
    <Modal title="Job library" onClose={onClose} wide footer={
      <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Close</button>
    }>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <label className="form-label">Add a job</label>
          <div className="flex gap-2 items-center">
            <input className="form-input flex-1" value={addName} onChange={e => setAddName(e.target.value)} placeholder="AH Techbar" />
            <select className="form-select grow-0 shrink-0 basis-24" value={addTone} onChange={e => setAddTone(e.target.value)}>
              {TONE_KEYS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className="form-input grow-0 shrink-0 basis-36" list="plow-sow-refs" value={addSowRef} onChange={e => setAddSowRef(e.target.value)} onBlur={prefillAddName} placeholder="AV-… (optional)" />
            <label className="flex items-center gap-1 text-[9px] text-gray-600 whitespace-nowrap" title="Assigning this job raises the £15 weekday or £20 weekend desk move allowance for that person and day (Overtime Policy 3.c)">
              <input type="checkbox" className="accent-navy" checked={addMove} onChange={e => setAddMove(e.target.checked)} />
              Desk move or install
            </label>
            <label className="flex items-center gap-1 text-[9px] text-gray-600 whitespace-nowrap" title="Assigning this job pays a quarter of the hourly rate on that day's shift for working away (Overtime Policy 2.a.vi)">
              <input type="checkbox" className="accent-navy" checked={addAway} onChange={e => setAddAway(e.target.checked)} />
              Working away
            </label>
            <button type="button" className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50" onClick={addJob} disabled={busy}>Add</button>
          </div>
          <p className="text-[9px] text-gray-400 mt-1.5">A SOW reference links the job to the real project, which is what will feed time logging. Any SOW reference works, not only the suggestions. Enter the SOW first and the job name is filled in from it, ready to edit.</p>
          <datalist id="plow-sow-refs">
            {sows.map(s => <option key={s.id} value={s.reference}>{(s.detail || '').slice(0, 60)}</option>)}
          </datalist>
        </div>

        {error && <p className="text-[10px] text-red-600 font-semibold">{error}</p>}

        <div>
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1">Jobs</div>
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            {work.length === 0 && <p className="text-[10px] text-gray-400 p-3">No jobs yet. Add the first one above.</p>}
            {work.map(j => <Row key={j.id} j={j} />)}
          </div>
        </div>

        {archived.length > 0 && (
          <div>
            <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1">Archived jobs</div>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              {archived.map(j => <Row key={j.id} j={j} />)}
            </div>
            <p className="text-[9px] text-gray-400 mt-1">Archived jobs are hidden from the cell editor. Weeks that used them are unchanged, and Restore brings one back.</p>
          </div>
        )}

        <div>
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1">Built-in statuses</div>
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            {statuses.map(j => <Row key={j.id} j={j} />)}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── The board's standby levels ────────────────────────────────────────────────
// Admin keeps the company list (Level 1, Escalation). A board's editors add
// their own underneath (SMS, ODAS) here, so the board stops needing a standby
// section per type of standby. Company levels are shown so the whole dropdown
// is visible, but only the board's own can be changed from here. Edits save as
// they are made, like the job library, so Cancel on the dialog does not undo
// them; the parent reloads the week so the pickers pick the change up.
// Module-level for the same remount reason as PersonAdder.
function StandbyLevelsBlock({ plowId, onChanged }) {
  const [levels, setLevels] = useState(null);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { setLevels(await api.get(`plows/${plowId}/standby-levels?include_inactive=1`)); }
    catch (e) { setError(e.message || 'The standby levels could not be loaded'); }
  }, [plowId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setError('');
    setBusy(true);
    try { await fn(); await load(); if (onChanged) onChanged(); }
    catch (e) { setError(e.message || 'The standby level could not be saved'); }
    setBusy(false);
  };
  const add = () => {
    const name = newName.trim();
    if (!name) { setError('Give the level a name'); return; }
    run(async () => { await api.post(`plows/${plowId}/standby-levels`, { name }); setNewName(''); });
  };
  const rename = (lvl) => {
    const name = editName.trim();
    if (!name) { setError('Give the level a name'); return; }
    run(async () => { await api.put(`plows/${plowId}/standby-levels/${lvl.id}`, { name }); setEditing(null); });
  };
  const retire = (lvl) => run(() => api.delete(`plows/${plowId}/standby-levels/${lvl.id}`));
  const restore = (lvl) => run(() => api.put(`plows/${plowId}/standby-levels/${lvl.id}`, { is_active: 1 }));

  const company = (levels || []).filter(l => !l.plow_id && l.is_active);
  const own = (levels || []).filter(l => l.plow_id);

  return (
    <div data-tour="plow-standby-levels">
      <label className="form-label">Standby levels</label>
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        {company.map(l => (
          <div key={l.id} className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100">
            <span className="text-[10px] font-semibold text-gray-700 flex-1">{l.name}</span>
            <span className="text-[9px] text-gray-400" title="Set in Admin, Settings, Standby Levels, and offered on every PLOW">company</span>
          </div>
        ))}
        {own.map(l => (
          <div key={l.id} className={`flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 ${l.is_active ? '' : 'opacity-50'}`}>
            {editing === l.id ? (
              <>
                <input className="form-input flex-1" value={editName} autoFocus
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); rename(l); } if (e.key === 'Escape') setEditing(null); }} />
                <button type="button" className="text-[9px] font-bold text-navy" onClick={() => rename(l)} disabled={busy}>Save</button>
                <button type="button" className="text-[9px] text-gray-500" onClick={() => setEditing(null)}>Cancel</button>
              </>
            ) : (
              <>
                <span className="text-[10px] font-semibold text-gray-700 flex-1">{l.name}{l.is_active ? '' : ' (retired)'}</span>
                <span className="text-[9px] text-blue-700 bg-blue-50 rounded px-1" title="Added on this PLOW">this board</span>
                {l.is_active ? (
                  <>
                    <button type="button" className="text-[9px] text-gray-500 hover:text-navy" onClick={() => { setEditing(l.id); setEditName(l.name); }} disabled={busy}>Rename</button>
                    <button type="button" className="text-[9px] text-red-500 hover:text-red-700" onClick={() => retire(l)} disabled={busy}
                      title="Stops it being offered for new cells. Days already on it keep the name.">Retire</button>
                  </>
                ) : (
                  <button type="button" className="text-[9px] text-gray-500 hover:text-navy" onClick={() => restore(l)} disabled={busy}>Restore</button>
                )}
              </>
            )}
          </div>
        ))}
        {levels && !own.length && (
          <div className="px-2 py-1.5 text-[9px] text-gray-400 border-b border-gray-100">No levels of this board's own yet.</div>
        )}
        <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50">
          <input className="form-input flex-1" value={newName} placeholder="Add a level for this board, such as SMS or ODAS"
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <button type="button" className="px-2.5 py-1.5 text-[10px] font-bold text-navy border border-navy rounded hover:bg-blue-50 disabled:opacity-40"
            onClick={add} disabled={busy || !newName.trim()}>Add</button>
        </div>
      </div>
      <p className="text-[9px] text-gray-400 mt-1">Offered in the Standby row and in Standby for the week, under the company levels. One Standby row can then carry every type of standby, one level per person a day. Changes here save straight away.</p>
      {error && <p className="text-[10px] text-red-600 font-semibold mt-1">{error}</p>}
    </div>
  );
}

// ── Board setup: create a PLOW, or reshape an existing one ────────────────────
// The weekdays a person row can be blocked on, stored as people.working_days is.
const BLOCK_WEEKDAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri']];

function SetupModal({ people, existing, currentUser, onClose, onSaved, onLevelsChanged }) {
  const isNew = !existing;
  const [name, setName] = useState(existing?.name || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [includeWeekends, setIncludeWeekends] = useState(!!existing?.include_weekends);
  const [hasDeadlines, setHasDeadlines] = useState(existing ? !!existing.has_deadlines : true);
  const [hasNotes, setHasNotes] = useState(!!existing?.has_notes);
  const [hasResourceRequests, setHasResourceRequests] = useState(!!existing?.has_resource_requests);
  const [currentWeekOnly, setCurrentWeekOnly] = useState(!!existing?.current_week_only);
  const [sections, setSections] = useState(existing?.sections?.map(s => ({ ...s })) || [{ label: 'AM', kind: 'assignment' }, { label: 'PM', kind: 'assignment' }]);
  // A row is a person or a label. A label row keeps its id, so renaming or
  // reordering it here does not orphan the cells already painted on it.
  const [members, setMembers] = useState(existing?.members?.filter(m => m.active).map(m => ({
    id: m.id, person_id: m.person_id, label: m.label || '',
    display_label: m.display_label || '', person_name: m.person_name,
    blocked_days: m.blocked_days || '',
  })) || []);
  const [newLabel, setNewLabel] = useState('');
  const [editors, setEditors] = useState(existing ? existing.access.filter(a => a.level === 'editor').map(a => a.person_id) : [currentUser?.id].filter(Boolean));
  const [viewers, setViewers] = useState(existing ? existing.access.filter(a => a.level === 'viewer').map(a => a.person_id) : []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const personName = (id) => {
    const p = people.find(x => x.id === id);
    return p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : id;
  };

  // A new label row is given an id here rather than by the server, so it can
  // be reordered and renamed before the first save like any other row.
  const addLabelRow = () => {
    const label = newLabel.trim();
    if (!label) return;
    setMembers(prev => [...prev, { id: `new-${Date.now()}-${prev.length}`, person_id: null, label }]);
    setNewLabel('');
  };

  const move = (arr, setArr, i, delta) => {
    const next = [...arr];
    const j = i + delta;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setArr(next);
  };

  const save = async () => {
    setError('');
    if (!name.trim()) { setError('Give the PLOW a name'); return; }
    if (!sections.some(s => s.kind !== 'shift')) { setError('A PLOW needs at least one assignment section'); return; }
    if (!editors.length) { setError('A PLOW needs at least one editor'); return; }
    if (members.some(m => !m.person_id && !m.label.trim())) { setError('Give every label row a name, or remove it'); return; }
    setSaving(true);
    const payloadSections = sections.map(s => ({ id: s.id, label: s.label, kind: s.kind }));
    const payloadMembers = members.map(m => (m.person_id
      ? { person_id: m.person_id, display_label: m.display_label || null, blocked_days: m.blocked_days || null }
      : { id: m.id, label: m.label }));
    try {
      if (isNew) {
        const r = await api.post('plows', {
          name: name.trim(), description: description.trim() || null,
          include_weekends: includeWeekends, has_deadlines: hasDeadlines,
          has_notes: hasNotes, has_resource_requests: hasResourceRequests,
          current_week_only: currentWeekOnly,
          sections: payloadSections, members: payloadMembers, editors, viewers,
        });
        onSaved(r.id);
      } else {
        await api.put(`plows/${existing.id}`, {
          name: name.trim(), description: description.trim() || null,
          include_weekends: includeWeekends, has_deadlines: hasDeadlines,
          has_notes: hasNotes, has_resource_requests: hasResourceRequests,
          current_week_only: currentWeekOnly,
        });
        await api.put(`plows/${existing.id}/sections`, { sections: payloadSections });
        await api.put(`plows/${existing.id}/members`, { members: payloadMembers });
        await api.put(`plows/${existing.id}/access`, { editors, viewers });
        onSaved(existing.id);
      }
    } catch (e) {
      setError(e.message || 'The PLOW could not be saved');
      setSaving(false);
    }
  };

  return (
    <Modal title={isNew ? 'Set up a PLOW' : `${existing.name}: settings`} onClose={onClose} xl footer={<>
      <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Cancel</button>
      <button type="button" className="px-4 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : isNew ? 'Create PLOW' : 'Save changes'}
      </button>
    </>}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div>
            <label className="form-label">Name</label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Service PLOW" />
          </div>
          <div>
            <label className="form-label">Description (optional)</label>
            <input className="form-input" value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <div>
            <label className="form-label">Sections of each day</label>
            <div className="space-y-1.5">
              {sections.map((s, i) => (
                <div key={s.id || `new-${i}`} className="flex gap-1.5 items-center">
                  <input className="form-input flex-1" value={s.label}
                    onChange={e => setSections(prev => prev.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))} />
                  <select className="form-select grow-0 shrink-0 basis-28" value={s.kind}
                    onChange={e => setSections(prev => prev.map((x, xi) => xi === i
                      ? { ...x, kind: e.target.value, label: sectionLabelFor(x.label, e.target.value) }
                      : x))}>
                    <option value="assignment">assignment</option>
                    <option value="text">text</option>
                    <option value="shift_time">shift time</option>
                    <option value="person">person</option>
                    <option value="standby">standby</option>
                  </select>
                  <button type="button" className="text-[10px] text-gray-400 hover:text-navy px-1" onClick={() => move(sections, setSections, i, -1)}>↑</button>
                  <button type="button" className="text-[10px] text-gray-400 hover:text-navy px-1" onClick={() => move(sections, setSections, i, 1)}>↓</button>
                  <button type="button" className="text-[10px] text-red-500 px-1" onClick={() => setSections(prev => prev.filter((_, xi) => xi !== i))}>✕</button>
                </div>
              ))}
            </div>
            <button type="button" className="mt-1.5 text-[10px] font-semibold text-navy hover:underline"
              onClick={() => setSections(prev => [...prev, { label: '', kind: 'assignment' }])}>
              + Add a section
            </button>
            <p className="text-[9px] text-gray-400 mt-1">An assignment section takes a job; a text section takes a note; a shift time section takes a start and an end time (a shift outside 7am to 6pm is a shift move for payroll); a person section links to someone, such as the project manager; a standby section puts someone on standby at a level, which raises the standby record for payroll once the week is confirmed. Examples: one "Day" section, or AM and PM, or AM, PM, Shift, Project Manager and Standby.</p>
          </div>

          {/* The board's own standby levels, once the board exists and has a
              Standby row (or is about to). Saved as they are made. */}
          {sections.some(sec => sec.kind === 'standby') && (
            isNew
              ? <p className="text-[9px] text-gray-400">Once the PLOW is created, its settings let you add standby levels of its own (such as SMS or ODAS) under the company ones.</p>
              : <StandbyLevelsBlock plowId={existing.id} onChanged={onLevelsChanged} />
          )}

          <div className="space-y-1.5">
            <label className="form-label">Options</label>
            {[
              ['Show Saturday and Sunday', includeWeekends, setIncludeWeekends],
              ['Deadlines panel', hasDeadlines, setHasDeadlines],
              ['Notes panel', hasNotes, setHasNotes],
              ['Resource requests panel', hasResourceRequests, setHasResourceRequests],
            ].map(([label, val, set]) => (
              <label key={label} className="flex items-center gap-2 text-[10px] text-gray-600">
                <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} /> {label}
              </label>
            ))}
            {/* The horizon: with this on, people on the board and its viewers
                see this week and the archive, and next week opens to them on
                Friday. Editors always see every week. Enforced by the server. */}
            <label className="flex items-start gap-2 text-[10px] text-gray-600" data-tour="plow-current-week-only">
              <input type="checkbox" className="mt-0.5" name="current_week_only" checked={currentWeekOnly} onChange={e => setCurrentWeekOnly(e.target.checked)} />
              <span>
                Only editors see beyond the current week
                <span className="block text-[9px] text-gray-400">People on the PLOW and viewers see this week and past weeks. Next week opens to them on Friday.</span>
              </span>
            </label>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="form-label">Rows on the PLOW (in order)</label>
            <div className="space-y-1.5 mb-2 max-h-80 overflow-y-auto pr-1">
              {members.map((m, i) => (
                <div key={m.person_id || m.id}>
                <div className="flex gap-1.5 items-center">
                  {m.person_id ? (
                    <>
                      <span className="text-[10px] font-semibold text-gray-700 flex-1 truncate">{m.person_name || personName(m.person_id)}</span>
                      <input className="form-input grow-0 shrink-0 basis-28" value={m.display_label} placeholder="Nickname"
                        onChange={e => setMembers(prev => prev.map((x, xi) => xi === i ? { ...x, display_label: e.target.value } : x))} />
                    </>
                  ) : (
                    <>
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide shrink-0 w-10">Label</span>
                      <input className="form-input flex-1" value={m.label} placeholder="SERVICING"
                        onChange={e => setMembers(prev => prev.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))} />
                    </>
                  )}
                  <button type="button" className="text-[10px] text-gray-400 hover:text-navy px-1" onClick={() => move(members, setMembers, i, -1)}>↑</button>
                  <button type="button" className="text-[10px] text-gray-400 hover:text-navy px-1" onClick={() => move(members, setMembers, i, 1)}>↓</button>
                  <button type="button" className="text-[10px] text-red-500 px-1" onClick={() => setMembers(prev => prev.filter((_, xi) => xi !== i))}>✕</button>
                </div>
                {/* Weekdays this person is never free for this board, such as
                    two days a week on another team's work. The grid shows them
                    as Blocked, every week, and nothing is written to a cell. */}
                {m.person_id && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[9px] text-gray-400 w-12 shrink-0">Blocked</span>
                    {BLOCK_WEEKDAYS.map(([k, label]) => {
                      const blocked = (m.blocked_days || '').split(',').includes(k);
                      const toggle = () => setMembers(prev => prev.map((x, xi) => {
                        if (xi !== i) return x;
                        const now = (x.blocked_days || '').split(',');
                        return { ...x, blocked_days: BLOCK_WEEKDAYS.map(([d]) => d).filter(d => (d === k) !== now.includes(d)).join(',') };
                      }));
                      return (
                        <button key={k} type="button" onClick={toggle} aria-pressed={blocked}
                          title={`${blocked ? 'Unblock' : 'Block'} ${m.person_name || personName(m.person_id)} on ${label} every week`}
                          className={`px-1.5 py-0.5 text-[9px] font-semibold rounded border ${blocked
                            ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200'}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
                </div>
              ))}
            </div>
            <PersonAdder
              people={people}
              exclude={members.filter(m => m.person_id).map(m => m.person_id)}
              label="Add a person…"
              onAdd={(id) => setMembers(prev => [...prev, { person_id: id, display_label: '', person_name: personName(id) }])}
            />
            {/* A lane for work that is nobody's in particular, which boards had
                been improvising by adding a person and nicknaming them. It has
                no person behind it, so it takes an assignment or a note and
                nothing that would raise a record against somebody. */}
            <div className="flex gap-2 items-center mt-1.5">
              <input
                className="form-input flex-1"
                value={newLabel}
                placeholder="Or a label row, for work that is nobody's: SERVICING"
                onChange={e => setNewLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLabelRow(); } }}
              />
              <button
                type="button"
                className="px-2.5 py-1.5 text-[10px] font-bold text-navy border border-navy rounded hover:bg-blue-50 disabled:opacity-40"
                disabled={!newLabel.trim()}
                onClick={addLabelRow}
              >Add</button>
            </div>
            <p className="text-[9px] text-gray-400 mt-1">Blocked days show on the grid as Blocked every week, for someone who spends set days on another team's work. A label row's day is a list rather than the board's sections: add as many tasks as there are, each a job or a note. It is not a person, so it has no shift time, no standby and no leave.</p>
          </div>

          <div>
            <label className="form-label">Editors (may assign and change settings)</label>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {editors.map(id => (
                <span key={id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded px-2 py-0.5 text-[9px] font-semibold">
                  {personName(id)}
                  <button type="button" className="hover:text-red-600" onClick={() => setEditors(prev => prev.filter(x => x !== id))}>✕</button>
                </span>
              ))}
            </div>
            <PersonAdder people={people} exclude={editors} label="Add an editor…" onAdd={(id) => setEditors(prev => [...prev, id])} />
          </div>

          <div>
            <label className="form-label">Viewers (read only)</label>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {viewers.map(id => (
                <span key={id} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded px-2 py-0.5 text-[9px] font-semibold">
                  {personName(id)}
                  <button type="button" className="hover:text-red-600" onClick={() => setViewers(prev => prev.filter(x => x !== id))}>✕</button>
                </span>
              ))}
            </div>
            <PersonAdder people={people} exclude={[...viewers, ...editors]} label="Add a viewer…" onAdd={(id) => setViewers(prev => [...prev, id])} />
            <p className="text-[9px] text-gray-400 mt-1.5">People on the PLOW can always see their own board without being listed here.</p>
          </div>
        </div>
      </div>
      {error && <p className="text-[10px] text-red-600 font-semibold mt-3">{error}</p>}
    </Modal>
  );
}

// ── The week's side panels: deadlines, notes, resource requests ───────────────
function WeekItemsPanel({ plow, weekStart, items, canEdit, onChanged }) {
  const { toast } = useToast();
  const groups = [
    plow.has_deadlines ? { kind: 'deadline', label: 'Deadlines' } : null,
    plow.has_notes ? { kind: 'note', label: 'Notes' } : null,
    plow.has_resource_requests ? { kind: 'resource_request', label: 'Resource requests' } : null,
  ].filter(Boolean);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  if (!groups.length) return null;

  const add = async (kind) => {
    const content = (drafts[kind] || '').trim();
    if (!content) return;
    setBusy(true);
    try {
      await api.post(`plows/${plow.id}/week-items`, { week_start: weekStart, kind, content });
      setDrafts(d => ({ ...d, [kind]: '' }));
      onChanged();
    } catch (e) { toast.error(e.message || 'The item could not be added'); }
    setBusy(false);
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`plows/${plow.id}/week-items/${id}`);
      onChanged();
    } catch (e) { toast.error(e.message || 'The item could not be removed'); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-tour="plow-panels">
      {groups.map(g => (
        <div key={g.kind} className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">{g.label}</div>
          <ul className="space-y-1 mb-2">
            {items.filter(i => i.kind === g.kind).map(i => (
              <li key={i.id} className="flex items-start gap-1.5 text-[10px] text-gray-700">
                <span className="flex-1 whitespace-pre-wrap">{i.content}</span>
                {canEdit && (
                  <button type="button" className="text-gray-300 hover:text-red-500 shrink-0" onClick={() => remove(i.id)} disabled={busy} aria-label={`Remove ${g.label.toLowerCase()} item`}>✕</button>
                )}
              </li>
            ))}
            {!items.some(i => i.kind === g.kind) && <li className="text-[9px] text-gray-300">Nothing this week</li>}
          </ul>
          {canEdit && (
            <div className="flex gap-1.5">
              <input
                className="form-input flex-1"
                value={drafts[g.kind] || ''}
                onChange={e => setDrafts(d => ({ ...d, [g.kind]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') add(g.kind); }}
                placeholder="Add…"
              />
              <button type="button" className="px-2.5 py-1 text-[10px] font-bold text-navy border border-navy rounded hover:bg-blue-50 disabled:opacity-40" onClick={() => add(g.kind)} disabled={busy}>Add</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Asking for access to a board you are not on ───────────────────────────────
// Every active board shows on the tab bar; a locked one opens this instead of
// the grid. One box, for the reason: that is what the editors decide on. The
// result, good or bad, is reported here in the dialog rather than at the top
// of the page.
function RequestAccessModal({ plow, onClose, onSent }) {
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    if (!message.trim()) { setError('Say briefly why you need access'); return; }
    setSaving(true);
    setError('');
    try {
      await api.post(`plows/${plow.id}/access-requests`, { message: message.trim() });
      onSent(plow.id);
    } catch (e) {
      setError(e.message || 'The request could not be sent');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Request access to ${plow.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 border border-gray-300 rounded hover:border-navy" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50" onClick={send} disabled={saving || !message.trim()}>
            {saving ? 'Sending…' : 'Request access'}
          </button>
        </>
      }
    >
      <p className="text-[10px] text-gray-500 mb-2">
        You are not on this PLOW. Its editors will be told, and can let you see the plan as a viewer.
      </p>
      <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1" htmlFor="plow-access-message">Why do you need access?</label>
      <textarea
        id="plow-access-message"
        className="form-textarea"
        rows={4}
        autoFocus
        value={message}
        onChange={e => { setMessage(e.target.value); if (error) setError(''); }}
        placeholder="For example: I cover the Glasgow moves and need to see who is booked where."
      />
      {error && <p className="text-[10px] text-red-600 mt-2">{error}</p>}
    </Modal>
  );
}

// A checkbox that can show "some of these": the job's tick when only part of
// its week is selected. The indeterminate flag is a DOM property, not an
// attribute, so it is set through a ref.
function TriStateCheckbox({ checked, indeterminate, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate && !checked; }, [indeterminate, checked]);
  return <input ref={ref} type="checkbox" className="accent-navy" checked={checked} onChange={onChange} aria-label={label} />;
}

// The weekly client report: the week's assignments grouped by the AV
// reference of their job, each ticked by default, downloaded as a spreadsheet
// in the shape of the client's own plan-of-works template. The manager unticks
// a whole job, or a single person's day, before downloading. Only jobs linked
// to a SOW are offered, since the reference is the row; the unlinked ones are
// named here so the manager can link them in the job library and come back.
function ExportReportModal({ data, onClose, onDownloaded }) {
  const [sowDetails, setSowDetails] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`plows/${data.plow.id}/linked-sows`)
      .then(r => { setSowDetails(Array.isArray(r) ? r : []); setError(''); })
      .catch(e => { setSowDetails([]); setError(e.message || 'The SOW details could not be loaded; the sheet will carry what the board knows'); });
  }, [data.plow.id]);

  const { groups, unlinked } = useMemo(
    () => groupExportTasks({ ...data, sowDetails: sowDetails || [] }),
    [data, sowDetails],
  );

  // Everything ticked to start with, once the groups are known.
  useEffect(() => {
    if (selected !== null) return;
    setSelected(new Set(groups.flatMap(g => g.tasks.map(t => t.key))));
  }, [groups, selected]);

  const sel = useMemo(() => selected || new Set(), [selected]);
  const toggleTask = (key) => setSelected(prev => {
    const next = new Set(prev || []);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleGroup = (g, on) => setSelected(prev => {
    const next = new Set(prev || []);
    for (const t of g.tasks) { if (on) next.add(t.key); else next.delete(t.key); }
    return next;
  });
  const setAll = (on) => setSelected(on ? new Set(groups.flatMap(g => g.tasks.map(t => t.key))) : new Set());

  const rows = useMemo(() => buildExportRows({ groups, selected: sel }), [groups, sel]);

  const download = () => {
    if (!rows.length) return;
    try {
      downloadPlowExport({ rows, plowName: data.plow.name, weekStart: data.week_start });
      onDownloaded(rows.length);
    } catch (e) { setError(e.message || 'The spreadsheet could not be built'); }
  };

  const loading = sowDetails === null;
  return (
    <Modal title={`Export report: ${fmtWeek(data.week_start)}`} onClose={onClose} wide footer={
      <>
        <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50"
          onClick={download}
          disabled={loading || selected === null || selected.size === 0}
          title={sel.size === 0 && !loading ? 'Tick at least one job to include' : undefined}
        >
          {loading ? 'Loading…' : `Download spreadsheet (${rows.length} job${rows.length === 1 ? '' : 's'})`}
        </button>
      </>
    }>
      <div className="space-y-3">
        <p className="text-[10px] text-gray-500">
          One row per AV reference, in the client&apos;s plan-of-works layout: site, project, Yes/No for each day, planned start and finish, and a line of description.
          Untick anything the client should not see. Columns the board does not hold (Area, Zone, CSO, permits, badges) are left blank to fill in.
        </p>
        {error && <p className="text-[10px] text-red-600">{error}</p>}

        {loading && <p className="text-[10px] text-gray-400">Loading SOW details…</p>}

        {!loading && groups.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-[11px] font-semibold text-gray-600 mb-1">Nothing to export this week</p>
            <p className="text-[10px] text-gray-400">No assigned job on this week carries an AV reference. Link jobs to their SOW in the job library and the report will pick them up.</p>
          </div>
        )}

        {!loading && groups.length > 0 && (
          <>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="text-gray-500">{sel.size} of {groups.reduce((n, g) => n + g.tasks.length, 0)} assignments ticked</span>
              <button type="button" className="font-semibold text-navy hover:underline" onClick={() => setAll(true)}>Tick all</button>
              <button type="button" className="font-semibold text-navy hover:underline" onClick={() => setAll(false)}>Untick all</button>
            </div>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {groups.map(g => {
                const on = g.tasks.filter(t => sel.has(t.key)).length;
                const all = on === g.tasks.length;
                return (
                  <div key={g.job.id} className={on ? '' : 'opacity-60'}>
                    <label className="flex items-start gap-2 px-3 py-2 bg-gray-50 cursor-pointer">
                      <span className="pt-0.5"><TriStateCheckbox checked={all} indeterminate={on > 0 && !all} onChange={() => toggleGroup(g, !all)} label={`Include ${g.reference}`} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold text-navy">{g.reference}</span>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${TONES[g.job.tone] || TONES.gray}`}>{g.job.name}</span>
                          {g.sow.client_name && <span className="text-[9px] text-gray-500">{g.sow.client_name}</span>}
                        </span>
                        <span className="block text-[9px] text-gray-500 truncate">
                          {[g.sow.detail, g.sow.location].filter(Boolean).join(' · ') || 'SOW details not available'}
                        </span>
                      </span>
                      <span className="text-[9px] text-gray-400 shrink-0">{on}/{g.tasks.length}</span>
                    </label>
                    <div className="px-3 py-1">
                      {g.tasks.map(t => (
                        <label key={t.key} className="flex items-center gap-2 py-0.5 text-[10px] cursor-pointer">
                          <input type="checkbox" className="accent-navy" checked={sel.has(t.key)} onChange={() => toggleTask(t.key)} />
                          <span className="font-semibold text-gray-700 basis-20 flex-none">{fmtDay(t.slot_date)} {fmtShort(t.slot_date)}</span>
                          <span className="text-gray-800 truncate">{t.person_name}</span>
                          {t.section_label && <span className="text-gray-400">{t.section_label}</span>}
                          {t.rag && <span className={`w-2 h-2 rounded-full shrink-0 ${RAG[t.rag]?.dot || ''}`} title={RAG[t.rag]?.label} />}
                          {t.note && <span className="text-gray-400 truncate italic">{t.note}</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {!loading && unlinked.length > 0 && (
          <p className="text-[10px] text-amber-700 bg-amber-50 rounded px-2 py-1.5">
            Not in the report, no AV reference: {unlinked.map(j => j.name).join(', ')}. Link them to their SOW in the job library to include them.
          </p>
        )}
      </div>
    </Modal>
  );
}

// ── Clear days: empty any days for any rows in one go ───────────────────────────
// After a copy forward the week usually needs whole days taking out for several
// people at once. Ticking a day here empties the whole cell, as "Clear this
// day" does: every section for a person, the task list for a label row. Only
// days holding something can be ticked, a day's header ticks that day down the
// board, and a row's name ticks that row across the week.
function ClearDaysModal({ data, members, onClose, onCleared }) {
  const [sel, setSel] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const jobById = useMemo(() => Object.fromEntries((data.jobs || []).map(j => [j.id, j])), [data]);
  // What each row holds on each day, keyed `${rowKey}|${date}`.
  const days = useMemo(() => {
    const out = {};
    for (const s of data.slots || []) (out[`${slotRowKey(s)}|${s.slot_date}`] ||= { slots: [], tasks: [] }).slots.push(s);
    for (const t of data.label_tasks || []) (out[`${t.member_id}|${t.task_date}`] ||= { slots: [], tasks: [] }).tasks.push(t);
    return out;
  }, [data]);
  // Everything on the day, named, for the hover; the cell shows the first.
  const contents = (day) => [...new Set([...day.slots, ...day.tasks].map(x => (x.job_id && jobById[x.job_id]?.name) || x.note
    || (x.standby_level_id && 'Standby') || (x.shift_start && `${x.shift_start}-${x.shift_end}`) || (x.linked_person_id && 'With someone') || '')
    .filter(Boolean))];
  const preview = (day) => {
    const names = contents(day);
    return names.length > 1 ? `${names[0]} +${names.length - 1}` : (names[0] || 'Filled');
  };
  const filled = (m, d) => !!days[`${rowKey(m)}|${d}`];
  const allKeys = members.flatMap(m => data.dates.filter(d => filled(m, d)).map(d => `${rowKey(m)}|${d}`));

  const toggleKeys = (keys) => setSel(prev => {
    const next = new Set(prev);
    const on = keys.every(k => next.has(k));
    for (const k of keys) { if (on) next.delete(k); else next.add(k); }
    return next;
  });
  const dayKeys = (d) => members.filter(m => filled(m, d)).map(m => `${rowKey(m)}|${d}`);
  const rowKeys = (m) => data.dates.filter(d => filled(m, d)).map(d => `${rowKey(m)}|${d}`);

  // What clearing takes with it, said before the button is pressed.
  const picked = [...sel].map(k => days[k]).filter(Boolean);
  const standbyDays = picked.filter(day => day.slots.some(s => s.standby_level_id)).length;
  const allowanceDays = picked.filter(day => day.slots.some(s => s.job_id && (jobById[s.job_id]?.is_desk_move || jobById[s.job_id]?.is_working_away))).length;
  const people = new Set([...sel].map(k => k.split('|')[0])).size;

  const clear = async () => {
    setError('');
    setSaving(true);
    try {
      const byKey = Object.fromEntries(members.map(m => [rowKey(m), m]));
      const cells = [...sel].map(k => {
        const [row, date] = k.split('|');
        return isLabelRow(byKey[row]) ? { member_id: row, date } : { person_id: row, date };
      });
      const r = await api.post(`plows/${data.plow.id}/weeks/${data.week_start}/clear`, { cells });
      onCleared(r);
    } catch (e) {
      setError(e.message || 'The days could not be cleared');
      setSaving(false);
    }
  };

  return (
    <Modal title={`Clear days · ${fmtWeek(data.week_start)}`} onClose={onClose} xl footer={<>
      <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Cancel</button>
      <button
        type="button"
        className="px-4 py-1.5 text-[10px] font-bold text-white bg-red-600 rounded hover:opacity-90 disabled:opacity-50"
        onClick={clear}
        disabled={saving || !sel.size}
      >
        {saving ? 'Clearing…' : sel.size ? `Clear ${sel.size} day${sel.size === 1 ? '' : 's'}` : 'Clear days'}
      </button>
    </>}>
      <div className="space-y-3">
        <p className="text-[10px] text-gray-500">
          Tick the days to empty. A day's heading ticks it for everyone; a name ticks that person's whole week. Empty days cannot be ticked.
        </p>
        {!allKeys.length ? (
          <p className="text-[10px] text-gray-400 p-4 text-center border border-gray-200 rounded">Nothing is planned this week, so there is nothing to clear.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded">
            <table className="w-full text-[9px] border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-2 py-1.5 font-semibold text-gray-500 border-b border-gray-200">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <TriStateCheckbox
                        checked={allKeys.every(k => sel.has(k))}
                        indeterminate={sel.size > 0}
                        onChange={() => toggleKeys(allKeys)}
                        label="Tick every day"
                      />
                      Everyone
                    </label>
                  </th>
                  {data.dates.map(d => {
                    const keys = dayKeys(d);
                    const on = keys.length > 0 && keys.every(k => sel.has(k));
                    return (
                      <th key={d} className="px-1 py-1.5 border-b border-gray-200">
                        <button
                          type="button"
                          onClick={() => toggleKeys(keys)}
                          disabled={!keys.length}
                          aria-pressed={on}
                          className={`w-full px-1 py-1 rounded font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed
                            ${on ? 'bg-red-50 text-red-700 border-red-300' : 'bg-white text-gray-600 border-gray-300 hover:border-navy'}`}
                        >
                          {fmtDay(d)} {fmtShort(d)}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {members.map(m => {
                  const keys = rowKeys(m);
                  return (
                    <tr key={rowKey(m)} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-2 py-1 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => toggleKeys(keys)}
                          disabled={!keys.length}
                          className="font-semibold text-navy hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-default text-left"
                        >
                          {personLabel(m)}
                        </button>
                      </td>
                      {data.dates.map(d => {
                        const key = `${rowKey(m)}|${d}`;
                        const day = days[key];
                        const on = sel.has(key);
                        return (
                          <td key={d} className="px-1 py-1">
                            {day ? (
                              <button
                                type="button"
                                onClick={() => toggleKeys([key])}
                                aria-pressed={on}
                                title={`${on ? 'Will be cleared' : 'Tick to clear'}: ${contents(day).join(', ') || 'filled'}`}
                                className={`w-full min-h-[28px] px-1 rounded border truncate transition-colors
                                  ${on ? 'bg-red-50 text-red-700 border-red-300 line-through' : 'bg-white text-gray-700 border-gray-200 hover:border-navy'}`}
                              >
                                {preview(day)}
                              </button>
                            ) : (
                              <span className="block text-center text-gray-300">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {sel.size > 0 && (
          <p className="text-[9px] text-gray-500">
            {sel.size} day{sel.size === 1 ? '' : 's'} across {people} row{people === 1 ? '' : 's'} will be emptied. This cannot be undone.
          </p>
        )}
        {standbyDays > 0 && (
          <p className="text-[9px] font-semibold text-amber-700">
            {standbyDays} of these days {standbyDays === 1 ? 'has' : 'have'} standby on it. Clearing cancels the standby, including any already confirmed for payroll.
          </p>
        )}
        {allowanceDays > 0 && (
          <p className="text-[9px] font-semibold text-amber-700">
            {allowanceDays} of these days {allowanceDays === 1 ? 'has' : 'have'} a desk move or working away job. Its allowance is cancelled with it.
          </p>
        )}
        {error && <p className="text-[10px] text-red-600 font-semibold">{error}</p>}
      </div>
    </Modal>
  );
}

// ── Standby for the week: one person, one level, every day, in one save ────────
// The Standby row was filled a day at a time, or by ticking days under "Also
// apply to" in the cell editor. Standby is planned by the week, so this puts
// one person on one level for the whole week from a single dialog. It makes
// the same slot save the cell editor does, so every rule is the server's and
// applied once: one level a day, a pending record per day, and the clash
// check, which refuses the whole week rather than half of it.
function AssignStandbyWeekModal({ data, onClose, onSaved }) {
  const standbySections = data.sections.filter(s => s.kind === 'standby');
  const members = data.members.filter(m => m.active && m.person_id);
  const levels = data.standby_levels || [];
  const [sectionId, setSectionId] = useState(standbySections[0]?.id || '');
  const [personId, setPersonId] = useState('');
  const [levelId, setLevelId] = useState(levels.length === 1 ? levels[0].id : '');
  const [note, setNote] = useState('');
  const [days, setDays] = useState(() => new Set(data.dates));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const dates = data.dates.filter(d => days.has(d));
  const hours = dates.reduce((sum, d) => sum + (standbySlotFor(d)?.hours || 0), 0);
  const member = members.find(m => m.person_id === personId);
  const level = levels.find(l => l.id === levelId);
  // Days this save would change: the person is already on standby in this row at another level.
  const replaced = dates.filter(d => data.slots.some(s => s.person_id === personId && s.slot_date === d && s.section_id === sectionId && s.standby_level_id && s.standby_level_id !== levelId));
  // Days the server would refuse: the same person on standby in another standby row.
  const clashes = dates.filter(d => data.slots.some(s => s.person_id === personId && s.slot_date === d && s.section_id !== sectionId && s.standby_level_id
    && standbySections.some(sec => sec.id === s.section_id)));

  const toggle = (d) => setDays(prev => {
    const next = new Set(prev);
    if (next.has(d)) next.delete(d); else next.add(d);
    return next;
  });

  const save = async () => {
    setError('');
    if (!personId) { setError('Pick who is on standby'); return; }
    if (!levelId) { setError('Pick a standby level'); return; }
    if (!dates.length) { setError('Tick at least one day'); return; }
    setSaving(true);
    try {
      await api.put(`plows/${data.plow.id}/slots`, {
        person_id: personId, section_id: sectionId, dates, standby_level_id: levelId, note: note.trim() || null,
      });
      onSaved({ days: dates.length, hours, member, level });
    } catch (e) {
      setError(e.message || 'Standby could not be assigned');
      setSaving(false);
    }
  };

  return (
    <Modal title={`Standby for the week · ${fmtWeek(data.week_start)}`} onClose={onClose} footer={<>
      <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Cancel</button>
      <button
        type="button"
        className="px-4 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50"
        onClick={save}
        disabled={saving || !personId || !levelId || !dates.length || clashes.length > 0}
        title={clashes.length ? 'Untick the clashing days first' : undefined}
      >
        {saving ? 'Assigning…' : `Put on standby (${dates.length} day${dates.length === 1 ? '' : 's'})`}
      </button>
    </>}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="form-label">Who</label>
            <select className="form-select" value={personId} onChange={e => setPersonId(e.target.value)} autoFocus>
              <option value="">Pick a person…</option>
              {members.map(m => <option key={m.person_id} value={m.person_id}>{personLabel(m)}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Standby level</label>
            <select className="form-select" value={levelId} onChange={e => setLevelId(e.target.value)}>
              <option value="">Pick a level…</option>
              {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>
        {standbySections.length > 1 && (
          <div>
            <label className="form-label">Row</label>
            <select className="form-select" value={sectionId} onChange={e => setSectionId(e.target.value)}>
              {standbySections.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="form-label">Days</label>
          <div className="flex flex-wrap gap-1.5">
            {data.dates.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => toggle(d)}
                aria-pressed={days.has(d)}
                className={`px-2 py-1 rounded text-[9px] font-semibold border transition-colors
                  ${days.has(d) ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-navy'}`}
              >
                {fmtDay(d)} {fmtShort(d)}
              </button>
            ))}
          </div>
          <p className="text-[9px] text-gray-400 mt-1">
            {dates.length
              ? <>{dates.length} day{dates.length === 1 ? '' : 's'}: {hours}h at £{STANDBY_RATE_PER_HOUR}/h, £{standbyPay(hours)}. Raised as pending standby; approved when the week is confirmed.</>
              : 'Tick the days on standby.'}
          </p>
        </div>
        <div>
          <label className="form-label">Note (optional)</label>
          <input className="form-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Shown on each day's cell" />
        </div>
        {personId && replaced.length > 0 && (
          <p className="text-[9px] font-semibold text-amber-700">
            {personLabel(member || {})} is already on standby at another level on {replaced.map(d => fmtDay(d)).join(', ')}: those days move to {level?.name || 'this level'} and go back to pending.
          </p>
        )}
        {personId && clashes.length > 0 && (
          <p className="text-[9px] font-semibold text-red-600">
            {personLabel(member || {})} is on standby in another row on {clashes.map(d => fmtDay(d)).join(', ')}. One person, one level a day: untick those days or clear the other row first.
          </p>
        )}
        {error && <p className="text-[10px] text-red-600 font-semibold">{error}</p>}
      </div>
    </Modal>
  );
}

// The standby rota export: the "24-7 Rota" spreadsheet, built from the
// Standby row. The editor picks a range (this Monday for eight weeks to
// start with, no further than standby is planned), sees how many slots it
// holds and which days have nobody at a level, and downloads one HTML file
// that shows today's cover first with tap-to-call numbers. Everything the
// file needs is inside it, so it can go by email or Teams and open anywhere.
// The same range also downloads as the spreadsheet the incident management
// system's Upload on-call rota screen takes, with the editor choosing which
// of the board's levels fills its Level 1 and Level 2 columns.
function ExportStandbyRotaModal({ data, currentUser, onClose, onDownloaded }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState(() => defaultStandbyRotaRange(todayISO, null));
  const [rota, setRota] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [narrowed, setNarrowed] = useState(false);
  const [format, setFormat] = useState('html');
  const [columns, setColumns] = useState(null);

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(range.from) && /^\d{4}-\d{2}-\d{2}$/.test(range.to) && range.to >= range.from;

  useEffect(() => {
    if (!valid) { setRota(null); return; }
    let live = true;
    setLoading(true);
    api.get(`plows/${data.plow.id}/standby-rota?from=${range.from}&to=${range.to}`)
      .then(r => {
        if (!live) return;
        setRota(r); setError('');
        // First load: pull the end of the range back to the last planned day,
        // once, so the default does not run off into empty weeks.
        if (!narrowed) {
          setNarrowed(true);
          const def = defaultStandbyRotaRange(todayISO, r.last_planned);
          if (def.to !== range.to) setRange(def);
        }
      })
      .catch(e => { if (live) { setRota(null); setError(e.message || 'The standby rota could not be loaded'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.plow.id, range.from, range.to, valid]);

  const model = useMemo(() => {
    if (!rota || !valid) return null;
    try { return buildStandbyRota({ from: range.from, to: range.to, slots: rota.slots, holidays: rota.holidays }); }
    catch { return null; }
  }, [rota, range.from, range.to, valid]);

  const gaps = model?.gaps || [];
  const gapDays = new Set(gaps.map(g => g.date)).size;

  // The upload's columns default to the first two levels in use, and fall
  // back to that when a new range no longer has the level that was picked.
  const levelIds = (model?.levels || []).map(l => l.id).join(',');
  useEffect(() => {
    if (!model) return;
    setColumns(c => (c && [c.level1, c.level2].every(id => !id || model.levels.some(l => l.id === id)) ? c : defaultOnCallLevels(model)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelIds]);
  const upload = useMemo(() => (model && columns ? buildOnCallUpload(model, columns) : null), [model, columns]);
  const noColumn = !columns || (!columns.level1 && !columns.level2);

  const download = () => {
    if (!model) return;
    if (format === 'upload') {
      if (!upload || noColumn) return;
      try {
        downloadXlsx([onCallUploadSheet(upload)], onCallUploadFilename(data.plow.name, range.from, range.to));
        onDownloaded(model.slot_count, range.from, range.to, 'upload');
      } catch (e) { setError(e.message || 'The file could not be built'); }
      return;
    }
    try {
      const html = renderStandbyRotaHtml(model, {
        plowName: data.plow.name,
        generatedAt: new Date().toISOString(),
        generatedBy: currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() : '',
      });
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = standbyRotaFilename(data.plow.name, range.from, range.to);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      onDownloaded(model.slot_count, range.from, range.to);
    } catch (e) { setError(e.message || 'The file could not be built'); }
  };

  return (
    <Modal title="Export standby rota" onClose={onClose} footer={
      <>
        <button type="button" className="px-3 py-1.5 text-[10px] font-semibold text-gray-600 hover:text-gray-800" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50"
          onClick={download}
          disabled={loading || !model || model.slot_count === 0 || (format === 'upload' && noColumn)}
          title={model && model.slot_count === 0 ? 'Nobody is on standby in this range' : format === 'upload' && noColumn ? 'Pick a level for at least one column' : undefined}
        >
          {loading ? 'Loading…' : format === 'upload'
            ? `Download spreadsheet${model ? ` (${model.days.length} day${model.days.length === 1 ? '' : 's'})` : ''}`
            : `Download rota${model ? ` (${model.slot_count} slot${model.slot_count === 1 ? '' : 's'})` : ''}`}
        </button>
      </>
    }>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Format">
          {[
            ['html', 'Rota to share', 'One .html file for email or Teams'],
            ['upload', 'On-call upload', '.xlsx for the incident system'],
          ].map(([value, label, hint]) => (
            <label key={value} className={`block border rounded-lg px-3 py-2 cursor-pointer ${format === value ? 'border-navy bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input type="radio" name="standby-export-format" value={value} checked={format === value} onChange={() => setFormat(value)} className="mr-1.5 align-middle" />
              <span className="text-[11px] font-semibold text-navy align-middle">{label}</span>
              <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>
            </label>
          ))}
        </div>
        {format === 'html' ? (
          <p className="text-[10px] text-gray-500">
            One file, opens anywhere, no sign-in: today&apos;s cover at the top with tap-to-call numbers, then each week of the range, and a click on any name for their contact details.
            Numbers come from People (work phone and personal mobile), so only editors can export it.
          </p>
        ) : (
          <p className="text-[10px] text-gray-500">
            A row per day in the columns Upload on-call rota takes: Date, Level 1, Number, Level 2, Number. Each person goes in by work email, or by name where People has no email.
            The number is their work phone if it is a mobile, otherwise their personal mobile.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">From</span>
            <input type="date" className="form-input w-full" value={range.from} max={range.to || undefined} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} />
          </label>
          <label className="block">
            <span className="block text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">To</span>
            <input type="date" className="form-input w-full" value={range.to} min={range.from || undefined} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} />
          </label>
        </div>
        {!valid && <p className="text-[10px] text-red-600">Pick a from and a to date, in that order.</p>}
        {error && <p className="text-[10px] text-red-600">{error}</p>}
        {rota && (
          <p className="text-[10px] text-gray-500">
            {rota.last_planned
              ? <>Standby is planned on this board up to <span className="font-semibold text-gray-700">{fmtLongDate(rota.last_planned)}</span>.</>
              : <>No standby has been planned on this board yet.</>}
          </p>
        )}
        {format === 'upload' && model && model.slot_count > 0 && columns && (
          <div className="grid grid-cols-2 gap-3">
            {[['level1', 'Level 1 column'], ['level2', 'Level 2 column']].map(([key, label]) => (
              <label key={key} className="block">
                <span className="block text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</span>
                <select className="form-input w-full" value={columns[key]} onChange={e => setColumns(c => ({ ...c, [key]: e.target.value }))}>
                  <option value="">Leave blank</option>
                  {model.levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </label>
            ))}
          </div>
        )}
        {format === 'upload' && model && model.slot_count > 0 && upload && (
          <div className="bg-gray-50 rounded-lg p-3 text-[10px] space-y-1">
            {noColumn
              ? <p className="text-red-600">Pick a level for at least one column.</p>
              : (
                <p className="text-gray-700">
                  <span className="font-semibold">{upload.rows.length} day{upload.rows.length === 1 ? '' : 's'}</span>, {fmtLongDate(range.from)} to {fmtLongDate(range.to)}.
                </p>
              )}
            {upload.left_out.length > 0 && (
              <p className="text-amber-700">Not in the file: {upload.left_out.map(l => l.name).join(', ')}. The upload has two levels.</p>
            )}
            {upload.doubled.length > 0 && (
              <p className="text-amber-700">
                The upload holds one person per level per day, so only the first goes in on {upload.doubled.length} day{upload.doubled.length === 1 ? '' : 's'}: {upload.doubled.slice(0, 3).map(d => `${fmtDay(d.date)} ${fmtShort(d.date)} ${d.column}, ${d.kept} (not ${d.dropped.join(', ')})`).join('; ')}{upload.doubled.length > 3 ? ' and more' : ''}.
              </p>
            )}
            {!noColumn && upload.gaps.length > 0 && (
              <p className="text-amber-700">
                {upload.gaps.length} blank cell{upload.gaps.length === 1 ? '' : 's'}: {upload.gaps.slice(0, 4).map(g => `${fmtDay(g.date)} ${fmtShort(g.date)} ${g.column}`).join('; ')}{upload.gaps.length > 4 ? ' and more' : ''}. A blank cell leaves whatever the incident system already holds for that day.
              </p>
            )}
            {Object.values(model.people).some(p => !p.work_phone && !p.personal_mobile) && (
              <p className="text-amber-700">
                No number in People for {Object.values(model.people).filter(p => !p.work_phone && !p.personal_mobile).map(p => p.name).join(', ')}, so their Number cells are blank.
              </p>
            )}
            {Object.values(model.people).some(p => !p.email) && (
              <p className="text-amber-700">
                No work email in People for {Object.values(model.people).filter(p => !p.email).map(p => p.name).join(', ')}, so they go in by name and the upload must find that exact name.
              </p>
            )}
          </div>
        )}
        {format === 'html' && model && model.slot_count > 0 && (
          <div className="bg-gray-50 rounded-lg p-3 text-[10px] space-y-1">
            <p className="text-gray-700">
              <span className="font-semibold">{model.slot_count} standby slot{model.slot_count === 1 ? '' : 's'}</span>
              {' '}across {model.levels.map(l => l.name).join(', ')} for {Object.keys(model.people).length} {Object.keys(model.people).length === 1 ? 'person' : 'people'}.
            </p>
            {gaps.length > 0 ? (
              <p className="text-amber-700">
                {gapDays} day{gapDays === 1 ? '' : 's'} with nobody at a level ({gaps.length} gap{gaps.length === 1 ? '' : 's'}): {gaps.slice(0, 4).map(g => `${fmtDay(g.date)} ${fmtShort(g.date)} ${g.level_name}`).join('; ')}{gaps.length > 4 ? '; …' : ''}. The file shows them as blanks.
              </p>
            ) : (
              <p className="text-emerald-700">Every level has somebody on every day of the range.</p>
            )}
            {Object.values(model.people).some(p => !p.work_phone && !p.personal_mobile) && (
              <p className="text-amber-700">
                No number in People for: {Object.values(model.people).filter(p => !p.work_phone && !p.personal_mobile).map(p => p.name).join(', ')}.
              </p>
            )}
          </div>
        )}
        {model && model.slot_count === 0 && (
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-[11px] font-semibold text-gray-600 mb-1">Nobody on standby in this range</p>
            <p className="text-[10px] text-gray-400">Fill the Standby row for these weeks, or pick different dates.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// The open requests on a board, shown to its editors above the grid. The
// default answer is viewer access, one click; an editor can be made later
// from Settings. Declining leaves the person free to ask again.
function PendingAccessRequests({ plowId, canEdit, toast }) {
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!plowId || !canEdit) { setRequests([]); return; }
    api.get(`plows/${plowId}/access-requests`).then(r => setRequests(r || [])).catch(() => setRequests([]));
  }, [plowId, canEdit]);
  useEffect(() => { load(); }, [load]);

  const answer = async (req, verb) => {
    setBusy(req.id);
    try {
      await api.post(`plows/${plowId}/access-requests/${req.id}/${verb}`, {});
      toast.success(verb === 'grant' ? `${req.person_name} can now view this PLOW` : `Request from ${req.person_name} declined`);
      setRequests(prev => prev.filter(r => r.id !== req.id));
    } catch (e) {
      toast.error(e.message || 'The request could not be answered');
    }
    setBusy(null);
  };

  if (!canEdit || !requests.length) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-tour="plow-access-requests">
      <p className="text-[10px] font-bold text-amber-800">
        {requests.length === 1 ? 'Somebody has asked to see this PLOW' : `${requests.length} people have asked to see this PLOW`}
      </p>
      {requests.map(req => (
        <div key={req.id} className="flex items-start gap-3 bg-white border border-amber-200 rounded p-2">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-gray-700">{req.person_name}</p>
            <p className="text-[10px] text-gray-500 whitespace-pre-wrap break-words">{req.message}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              className="px-2.5 py-1 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90 disabled:opacity-50"
              disabled={busy === req.id}
              onClick={() => answer(req, 'grant')}
            >Give viewer access</button>
            <button
              type="button"
              className="px-2 py-1 text-[10px] font-semibold text-gray-500 hover:text-red-600 disabled:opacity-50"
              disabled={busy === req.id}
              onClick={() => answer(req, 'decline')}
            >Decline</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── The page ──────────────────────────────────────────────────────────────────
export default function PlowPage({ initialPlowId }) {
  const { currentUser } = useApp();
  const { toast } = useToast();
  const [plows, setPlows] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [requestFor, setRequestFor] = useState(null);
  const [week, setWeek] = useState(weekMonday());
  // 'week' is the whole board; 'month' is the signed-in person's own row
  // across the month, a line per week.
  const [view, setView] = useState('week');
  const [month, setMonth] = useState(() => todayIso().slice(0, 7));
  const [data, setData] = useState(null);
  const [weekError, setWeekError] = useState('');
  const [loadingWeek, setLoadingWeek] = useState(false);
  const [people, setPeople] = useState([]);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupConfig, setSetupConfig] = useState(null);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [cellEdit, setCellEdit] = useState(null);
  // A non-editor's tap opens the read-only detail sheet instead of the editor.
  const [cellDetail, setCellDetail] = useState(null);
  // Expanded shows the "+" bookable-slot hint on every empty assignment row;
  // compact hides it so more of the week fits on screen. Editors only, and
  // remembered per browser. Viewers never see hints: nothing is bookable.
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem('plow.expanded') !== 'compact'; } catch { return true; }
  });
  const toggleExpanded = () => setExpanded(v => {
    try { localStorage.setItem('plow.expanded', v ? 'compact' : 'expanded'); } catch { /* private mode */ }
    return !v;
  });
  const [copying, setCopying] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [rotaOpen, setRotaOpen] = useState(false);
  const [standbyWeekOpen, setStandbyWeekOpen] = useState(false);
  const [standbyCalendarOpen, setStandbyCalendarOpen] = useState(false);

  // The board opened last time, per person and per browser, so a deliberate
  // choice sticks between visits. Storage can be unavailable (a private
  // window, a locked-down browser), in which case the ranking decides.
  const rememberKey = `plow.lastBoard.${currentUser?.id || 'anon'}`;
  const readRemembered = () => { try { return localStorage.getItem(rememberKey); } catch (_) { return null; } };

  // The board named by the URL (a notification link) that has already been
  // acted on. It is honoured whenever it changes, and only then: a tab
  // clicked afterwards must not snap back the next time the list reloads.
  const handledUrlId = useRef(null);

  const loadPlows = useCallback((preferId) => {
    return api.get('plows').then(r => {
      const list = r || [];
      setPlows(list);
      setActiveId(prev => {
        // A board asked for outright (a save) wins; otherwise the one already
        // open stays; on first load the URL's board; otherwise the most
        // relevant.
        const requested = preferId || prev || (handledUrlId.current ? null : initialPlowId) || null;
        return pickDefaultPlow(list, { remembered: readRemembered(), requested });
      });
    }).catch(() => setPlows([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadPlows(); }, [loadPlows]);
  useEffect(() => {
    if (!initialPlowId || initialPlowId === handledUrlId.current || !plows) return;
    if (!plows.some(p => p.id === initialPlowId)) return;
    handledUrlId.current = initialPlowId;
    setActiveId(initialPlowId);
  }, [initialPlowId, plows]);
  useEffect(() => {
    api.get('people', { status: 'active' }).then(r => setPeople(r || [])).catch(() => {});
  }, []);

  const activePlow = useMemo(() => (plows || []).find(p => p.id === activeId) || null, [plows, activeId]);
  // A locked board can be the one on screen (everyone's boards are locked
  // to a new starter), but it is not worth remembering: next time the
  // ranking should have another go.
  const activeLocked = !!activePlow && !activePlow.my_level;
  // Switching to a current-week-only board while on My month lands on Week.
  const monthAllowed = myMonthAvailable(activePlow);
  useEffect(() => {
    if (!monthAllowed && view === 'month') setView('week');
  }, [monthAllowed, view]);
  useEffect(() => {
    if (!activeId || activeLocked) return;
    try { localStorage.setItem(rememberKey, activeId); } catch (_) { /* storage unavailable */ }
  }, [activeId, activeLocked, rememberKey]);

  const loadWeek = useCallback(() => {
    if (!activeId || activeLocked) { setData(null); setWeekError(''); return Promise.resolve(); }
    setLoadingWeek(true);
    return api.get(`plows/${activeId}/week`, { start: week })
      .then(r => { setData(r); setWeekError(''); setLoadingWeek(false); })
      .catch(e => {
        // A refused or failed week is reported where the grid would be, and
        // the previous board's grid is taken down rather than left standing
        // under another board's name.
        setData(null);
        setWeekError(e.message || 'The week could not be loaded');
        setLoadingWeek(false);
      });
  }, [activeId, activeLocked, week]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadWeek(); }, [loadWeek]);

  const canEdit = !!data?.plow?.can_edit;

  // The guided tour, for people arriving from the spreadsheet. Auto-opens the
  // first time only; the "?" beside the heading replays it.
  const tour = useFeatureTour(PLOW_TOUR_ID, { enabled: plows !== null });

  const slotMap = useMemo(() => {
    const out = {};
    for (const s of (data?.slots || [])) {
      out[`${slotRowKey(s)}|${s.slot_date}|${s.section_id}`] = s;
    }
    return out;
  }, [data]);
  const jobById = useMemo(() => {
    const out = {};
    for (const j of (data?.jobs || [])) out[j.id] = j;
    return out;
  }, [data]);
  // A label row's tasks, by row and day, in the order they were saved.
  const labelTaskMap = useMemo(() => {
    const out = {};
    for (const t of (data?.label_tasks || [])) {
      const key = `${t.member_id}|${t.task_date}`;
      (out[key] ||= []).push(t);
    }
    for (const list of Object.values(out)) list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return out;
  }, [data]);
  // Derived absences, keyed like the slots: one per person per date at most.
  const absenceMap = useMemo(() => {
    const out = {};
    for (const a of (data?.absences || [])) out[`${a.person_id}|${a.date}`] = a;
    return out;
  }, [data]);

  // Active members, plus anyone deactivated who still has cells this week, so
  // a past week keeps every column it had. Someone viewing their own board
  // finds themselves fastest at the top, so their own row is pulled ahead of
  // everyone else's and then highlighted below.
  const visibleMembers = useMemo(() => {
    if (!data) return [];
    const rows = data.members.filter(m =>
      m.active || data.slots.some(s => slotRowKey(s) === rowKey(m)));
    if (!currentUser?.id) return rows;
    const mine = rows.filter(m => m.person_id === currentUser.id);
    const rest = rows.filter(m => m.person_id !== currentUser.id);
    return [...mine, ...rest];
  }, [data, currentUser]);

  const openSettings = async () => {
    try {
      const cfg = await api.get(`plows/${activeId}`);
      setSetupConfig(cfg);
      setSetupOpen(true);
    } catch (e) { toast.error(e.message || 'The settings could not be loaded'); }
  };

  // The Standby row's week at a glance, and the manager check: every pending
  // standby record this board raised for the week is approved in one go.
  const standbySectionIds = new Set((data?.sections || []).filter(s => s.kind === 'standby').map(s => s.id));
  const onCallCells = (data?.slots || []).filter(s => standbySectionIds.has(s.section_id) && s.standby_level_id);
  const onCallPending = onCallCells.filter(s => s.standby_status === 'pending');
  const onCallHours = onCallCells.reduce((sum, s) => sum + (parseFloat(s.standby_hours) || 0), 0);
  const [confirming, setConfirming] = useState(false);
  const confirmStandby = async () => {
    if (!data?.plow?.id || !onCallPending.length) return;
    const pendingHours = onCallPending.reduce((sum, s) => sum + (parseFloat(s.standby_hours) || 0), 0);
    if (!window.confirm(`Confirm ${onCallPending.length} standby slot${onCallPending.length === 1 ? '' : 's'} for ${fmtWeek(week)} (${pendingHours}h, £${standbyPay(pendingHours)})? This approves them for payroll.`)) return;
    setConfirming(true);
    try {
      const r = await api.post(`plows/${data.plow.id}/weeks/${week}/confirm-standby`, {});
      toast.success(`${r.confirmed} standby slot${r.confirmed === 1 ? '' : 's'} confirmed for ${fmtWeek(week)}`);
      loadWeek();
    } catch (e) {
      toast.error(e.message || 'The week could not be confirmed');
    }
    setConfirming(false);
  };

  const copyForward = async () => {
    setCopying(true);
    try {
      const r = await api.post(`plows/${activeId}/weeks/${week}/copy-forward`, {});
      toast.success(r.copied
        ? `Copied ${r.copied} cell${r.copied === 1 ? '' : 's'} from last week`
        : 'Nothing to copy: last week is empty or this week is already filled');
      loadWeek();
    } catch (e) { toast.error(e.message || 'Last week could not be copied'); }
    setCopying(false);
  };

  const today = todayIso();
  const isPastWeek = week < weekMonday();
  // The horizon on a current-week-only board: the server returns the last
  // Monday this person may open (null for editors and for boards without the
  // setting), and the next-week arrow stops there rather than loading a week
  // that would be refused.
  const visibleUntil = data?.visible_until || null;
  const atHorizon = !!visibleUntil && week >= visibleUntil;
  // Weekend columns are almost always empty (the sheet kept them as thin
  // rows), so they take a narrower track: seven days then fit on screen
  // instead of pushing Sunday behind a sideways scroll.
  const gridCols = data
    ? {
      gridTemplateColumns: `minmax(120px, 1.1fr) ${data.dates
        .map(d => isWeekendIso(d) ? 'minmax(44px, 0.45fr)' : 'minmax(96px, 1fr)')
        .join(' ')}`,
    }
    : {};
  const gridMinWidth = data
    ? 130 + data.dates.reduce((w, d) => w + (isWeekendIso(d) ? 44 : 96), 0)
    : 0;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-base font-extrabold text-navy">PLOW</h2>
        <button
          type="button"
          onClick={tour.start}
          aria-label="Replay the guided tour of this page"
          title="Replay the guided tour of this page"
          className="w-[23px] h-[23px] shrink-0 rounded-full border border-gray-300 bg-white
            text-gray-500 text-[12px] font-semibold leading-none flex items-center justify-center
            transition-colors hover:border-navy hover:text-navy hover:bg-gray-50"
        >?</button>
        <span className="text-[10px] text-gray-400">Plan of the week</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            data-tour="plow-setup"
            className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90"
            onClick={() => { setSetupConfig(null); setSetupOpen(true); }}
          >+ New PLOW</button>
        </div>
      </div>

      {plows === null && <p className="text-[10px] text-gray-400">Loading…</p>}

      {plows !== null && plows.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-[11px] font-semibold text-gray-600 mb-1">No PLOWs yet</p>
          <p className="text-[10px] text-gray-400 mb-3">Set up the first board: name it, pick its sections (Day, AM/PM, Shift time), and put people on it.</p>
          <button type="button" className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90" onClick={() => { setSetupConfig(null); setSetupOpen(true); }}>
            Set up a PLOW
          </button>
        </div>
      )}

      {plows !== null && plows.length > 0 && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1 flex-wrap" data-tour="plow-boards">
              {/* Every active board is a tab, including the ones this person
                  cannot open. A locked tab does not switch the grid: it opens
                  the request dialog, or says the request is already in. */}
              {plows.filter(p => p.status === 'active' || p.id === activeId).map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => p.my_level ? setActiveId(p.id) : setRequestFor(p)}
                  disabled={!p.my_level && p.request_pending}
                  title={p.my_level ? undefined : (p.request_pending ? 'Access requested: waiting for an editor to answer' : 'You are not on this PLOW. Request access')}
                  className={`px-3 py-1.5 rounded text-[10px] font-bold border transition-colors inline-flex items-center gap-1.5
                    ${p.id === activeId ? 'bg-navy text-white border-navy'
                      : p.my_level ? 'bg-white text-gray-600 border-gray-300 hover:border-navy'
                      : 'bg-gray-50 text-gray-400 border-dashed border-gray-300 hover:border-navy hover:text-gray-600 disabled:hover:border-gray-300 disabled:hover:text-gray-400 disabled:cursor-default'}`}
                >
                  {!p.my_level && <span aria-hidden="true" className="text-[9px]">{p.request_pending ? '⏳' : '🔒'}</span>}
                  {p.name}{p.status === 'archived' ? ' (archived)' : ''}
                  {!p.my_level && p.request_pending && <span className="text-[8px] font-semibold uppercase tracking-wide">requested</span>}
                </button>
              ))}
            </div>

            {/* The arrows sit either side of the week they step, and Today is
                beyond them rather than between them. It used to read ‹ Today ›
                with 21x27px buttons 6px apart, so a thumb aiming at next week
                landed on the one control that jumps back to the current week:
                reported as "the date resets itself". Each control is a 44px
                target, and the label holds its width so they stop moving as
                the date changes length. */}
            <div className="ml-auto flex rounded border border-gray-300 overflow-hidden" data-tour="plow-view">
              {[['week', 'Week'], ['month', 'My month']].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  disabled={v === 'month' && !monthAllowed}
                  title={v === 'month' && !monthAllowed ? 'Only editors see beyond the current week on this PLOW, so My month is not available' : undefined}
                  onClick={() => {
                    // The month the week on screen mostly falls in, so the
                    // switch lands where the person was looking.
                    if (v === 'month' && view !== 'month') setMonth(addDays(week, 3).slice(0, 7));
                    setView(v);
                  }}
                  className={`min-h-[44px] px-3 text-[10px] font-semibold ${view === v ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'} disabled:text-gray-300 disabled:hover:bg-white disabled:cursor-not-allowed`}
                >{label}</button>
              ))}
            </div>

            {view === 'month' && (
              <div className="flex items-center gap-1.5 flex-wrap" data-tour="plow-month-nav">
                <button
                  type="button"
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center text-[15px] leading-none border border-gray-300 rounded hover:border-navy"
                  onClick={() => setMonth(m => shiftMonth(m, -1))}
                  aria-label="Previous month"
                >‹</button>
                <span className="text-[10px] font-bold text-gray-700 text-center px-1 min-w-[132px]">
                  {new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                </span>
                <button
                  type="button"
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center text-[15px] leading-none border border-gray-300 rounded hover:border-navy"
                  onClick={() => setMonth(m => shiftMonth(m, 1))}
                  aria-label="Next month"
                >›</button>
                <button
                  type="button"
                  className="ml-3 min-w-[44px] min-h-[44px] px-3 text-[10px] font-semibold border border-gray-300 rounded hover:border-navy"
                  onClick={() => setMonth(todayIso().slice(0, 7))}
                >Today</button>
              </div>
            )}

            {view === 'week' && (
            <div className="flex items-center gap-1.5 flex-wrap" data-tour="plow-week-nav">
              <button
                type="button"
                className="min-w-[44px] min-h-[44px] flex items-center justify-center text-[15px] leading-none border border-gray-300 rounded hover:border-navy"
                onClick={() => setWeek(w => addDays(w, -7))}
                aria-label="Previous week"
              >‹</button>
              <span className="text-[10px] font-bold text-gray-700 text-center px-1 min-w-[132px]">{fmtWeek(week)}</span>
              <button
                type="button"
                className="min-w-[44px] min-h-[44px] flex items-center justify-center text-[15px] leading-none border border-gray-300 rounded hover:border-navy disabled:opacity-40 disabled:hover:border-gray-300 disabled:cursor-not-allowed"
                onClick={() => setWeek(w => addDays(w, 7))}
                aria-label="Next week"
                disabled={atHorizon}
                title={atHorizon ? 'Next week opens to you from Friday' : undefined}
              >›</button>
              <button
                type="button"
                className="ml-3 min-w-[44px] min-h-[44px] px-3 text-[10px] font-semibold border border-gray-300 rounded hover:border-navy"
                onClick={() => setWeek(weekMonday())}
              >Today</button>
              {isPastWeek && <span className="text-[9px] font-semibold bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">past week</span>}
              {atHorizon && <span className="text-[9px] font-semibold bg-amber-50 text-amber-700 rounded px-1.5 py-0.5" title="Only editors can see further ahead">next week from Friday</span>}
            </div>
            )}
          </div>

          {data && view === 'week' && (
            <div className="flex items-center gap-2 flex-wrap">
              {standbySectionIds.size > 0 && onCallCells.length > 0 && (
                <span className="text-[9px] text-gray-500" title="Standby planned on this board this week">
                  Standby: {onCallCells.length} slot{onCallCells.length === 1 ? '' : 's'} · {onCallHours}h · £{standbyPay(onCallHours)}
                  {onCallPending.length > 0
                    ? <span className="ml-1 font-semibold bg-amber-100 text-amber-800 rounded px-1">{onCallPending.length} pending</span>
                    : <span className="ml-1 font-semibold bg-emerald-100 text-emerald-800 rounded px-1">all confirmed</span>}
                </span>
              )}
              {canEdit && standbySectionIds.size > 0 && onCallPending.length > 0 && (
                <button type="button" className="px-2.5 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50" onClick={confirmStandby} disabled={confirming}
                  title="Approve this week's standby for payroll">
                  {confirming ? 'Confirming…' : 'Confirm week'}
                </button>
              )}
              {canEdit && (
                <>
                  <button type="button" data-tour="plow-copy-forward" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50 disabled:opacity-50" onClick={copyForward} disabled={copying}>
                    {copying ? 'Copying…' : 'Copy last week'}
                  </button>
                  <button type="button" data-tour="plow-jobs" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50" onClick={() => setJobsOpen(true)}>
                    Job library
                  </button>
                  <button type="button" data-tour="plow-export" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50" onClick={() => setExportOpen(true)}>
                    Export report
                  </button>
                  {standbySectionIds.size > 0 && (
                    <button type="button" data-tour="plow-standby-week" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50 disabled:opacity-50" onClick={() => setStandbyWeekOpen(true)}
                      disabled={!(data.standby_levels || []).length}
                      title={(data.standby_levels || []).length ? 'Put one person on a standby level for every day of this week in one go' : 'No standby levels are set up yet'}>
                      Standby for the week
                    </button>
                  )}
                  {standbySectionIds.size > 0 && (
                    <button type="button" data-tour="plow-standby-calendar" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50 disabled:opacity-50" onClick={() => setStandbyCalendarOpen(true)}
                      disabled={!(data.standby_levels || []).length}
                      title={(data.standby_levels || []).length ? 'Plan standby months ahead: pick a person and a level, then drag across days' : 'No standby levels are set up yet'}>
                      Standby calendar
                    </button>
                  )}
                  {standbySectionIds.size > 0 && (
                    <button type="button" data-tour="plow-standby-export" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50" onClick={() => setRotaOpen(true)}
                      title="Download who is on standby, with contact numbers, as a file to share">
                      Export standby rota
                    </button>
                  )}
                  <button type="button" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50" onClick={openSettings}>
                    Settings
                  </button>
                  <button type="button" data-tour="plow-clear" className="px-2.5 py-1 text-[10px] font-semibold text-red-600 border border-red-300 rounded hover:bg-red-50" onClick={() => setClearOpen(true)}
                    title="Empty several days for several people in one go">
                    Clear
                  </button>
                  <label data-tour="plow-expanded" className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer select-none ml-1"
                    title={expanded ? 'Showing a + on every empty slot. Untick for a compact grid.' : 'Empty slots are hidden to fit more on screen. Tick to show a + on each.'}>
                    <input type="checkbox" className="accent-navy" checked={expanded} onChange={toggleExpanded} />
                    Expanded view
                  </label>
                </>
              )}
              {/* Viewers and members see the standby calendar too, read only:
                  who is covering when, months ahead, without paging weeks. */}
              {!canEdit && standbySectionIds.size > 0 && (data.standby_levels || []).length > 0 && (
                <button type="button" data-tour="plow-standby-calendar" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50" onClick={() => setStandbyCalendarOpen(true)}
                  title="See who is on standby, a month at a time">
                  Standby calendar
                </button>
              )}
              {!canEdit && data.plow.my_level && (
                <span className="text-[9px] text-gray-400">You are {data.plow.my_level === 'viewer' ? 'a viewer' : 'on this PLOW'}: the grid is read only for you.</span>
              )}
            </div>
          )}

          {data && <PendingAccessRequests plowId={data.plow.id} canEdit={canEdit} toast={toast} />}

          {/* Every board this person could open is locked (a new starter, or
              somebody who is not on any board yet). The grid's place explains
              and offers the request, rather than a bare refusal. */}
          {activeLocked && (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-[11px] font-semibold text-gray-600 mb-1">You are not on {activePlow.name}</p>
              <p className="text-[10px] text-gray-400 mb-3">
                {activePlow.request_pending
                  ? 'You have asked for access. Its editors have been told and one of them will answer.'
                  : 'Ask its editors for access and they can let you see the plan as a viewer.'}
              </p>
              {!activePlow.request_pending && (
                <button type="button" className="px-3 py-1.5 text-[10px] font-bold text-white bg-navy rounded hover:opacity-90" onClick={() => setRequestFor(activePlow)}>
                  Request access
                </button>
              )}
            </div>
          )}

          {/* A 7-day board needs the width the side panels would take on a
              middling screen, so weekend boards keep the panels below the
              grid until the screen is genuinely wide. */}
          <div className={`${activeLocked ? 'hidden ' : ''}grid grid-cols-1 ${view === 'month' ? '' : data?.plow?.include_weekends ? '2xl:grid-cols-[1fr_270px]' : 'xl:grid-cols-[1fr_270px]'} gap-3 items-start`}>
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-tour="plow-grid">
              {view === 'month' && activePlow && !activeLocked && (
                <MyMonthGrid
                  plow={activePlow}
                  month={month}
                  personId={currentUser?.id}
                  onOpenWeek={(start) => { setWeek(start); setView('week'); }}
                />
              )}
              {view === 'week' && loadingWeek && !data && !weekError && <p className="text-[10px] text-gray-400 p-4">Loading…</p>}
              {view === 'week' && weekError && !data && (
                <div className="p-6 text-center">
                  <p className="text-[10px] font-semibold text-gray-600 mb-2">{weekError}</p>
                  <button type="button" className="px-2.5 py-1 text-[10px] font-semibold text-navy border border-navy rounded hover:bg-blue-50" onClick={() => setWeek(weekMonday())}>
                    Back to this week
                  </button>
                </div>
              )}
              {view === 'week' && data && (
                <div style={{ minWidth: `${gridMinWidth}px` }}>
                  <div className="grid border-b border-gray-200 bg-gray-50" style={gridCols}>
                    <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wide px-3 py-2">Person</div>
                    {data.dates.map(d => (
                      <div key={d} className={`px-1 py-2 border-l border-gray-200 text-center min-w-0 ${isWeekendIso(d) ? 'bg-gray-100' : ''}`}>
                        <div className={`text-[8px] font-bold uppercase ${d === today ? 'text-blue-600' : 'text-gray-400'}`}>{fmtDay(d)}</div>
                        <div className={`text-[10px] font-semibold ${d === today ? 'text-blue-700' : 'text-gray-700'}`}>{fmtShort(d)}</div>
                      </div>
                    ))}
                  </div>
                  {visibleMembers.map((m, i) => {
                    // Alternating bands, so a Friday cell still reads as
                    // belonging to the name seven columns to its left. The
                    // row carries the tone and its cells sit transparent over
                    // it; see plowRowTone.js.
                    const tone = plowRowTone(i, { isCurrentUser: m.person_id === currentUser?.id });
                    return (
                    <div key={rowKey(m)} className={`grid border-b border-gray-100 last:border-b-0 ${tone.row}`} style={gridCols}>
                      <div className={`px-3 py-2 flex items-center gap-2 border-r border-gray-200 ${tone.name}`}>
                        {/* A label row is not a person, so it gets a plain
                            marker rather than somebody's initials and colour,
                            which is what made the workaround read as a member
                            of staff on the board. */}
                        {isLabelRow(m) ? (
                          <span className="w-5 h-5 rounded border border-dashed border-gray-300 bg-gray-50 text-gray-400 text-[8px] font-bold flex items-center justify-center shrink-0" title="Not a person">
                            #
                          </span>
                        ) : (
                          <Avatar as="span" personId={m.person_id}
                            className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0"
                            style={{ background: m.avatar_color || '#64748b' }}
                          >
                            {(m.person_name || '?').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
                          </Avatar>
                        )}
                        <span className="text-[10px] font-semibold text-gray-800 truncate" title={personLabel(m)}>
                          {personLabel(m)}{!m.active && <span className="text-gray-400 font-normal"> (left)</span>}
                        </span>
                      </div>
                      {data.dates.map(d => {
                        // A label row's day is its task list, not a cell per
                        // section.
                        if (isLabelRow(m)) {
                          return (
                            <LabelTaskCell
                              key={d}
                              tasks={labelTaskMap[`${m.id}|${d}`] || []}
                              jobById={jobById}
                              isToday={d === today}
                              weekend={isWeekendIso(d)}
                              clickable={m.active === 1}
                              hints={canEdit && expanded}
                              onClick={() => canEdit ? setCellEdit({ rowId: rowKey(m), date: d }) : setCellDetail({ rowId: rowKey(m), date: d })}
                            />
                          );
                        }
                        return (
                          <SlotCell
                            key={d}
                            sections={data.sections}
                            slots={daySlots(data, (secId) => slotMap[`${rowKey(m)}|${d}|${secId}`], jobById)}
                            isToday={d === today}
                            weekend={isWeekendIso(d)}
                            clickable={m.active === 1}
                            hints={canEdit && expanded}
                            absence={absenceMap[`${rowKey(m)}|${d}`]}
                            onClick={() => canEdit ? setCellEdit({ rowId: rowKey(m), date: d }) : setCellDetail({ rowId: rowKey(m), date: d })}
                          />
                        );
                      })}
                    </div>
                    );
                  })}
                  {visibleMembers.length === 0 && (
                    <p className="text-[10px] text-gray-400 p-4">Nobody is on this PLOW yet. Add people in Settings.</p>
                  )}
                </div>
              )}
            </div>

            {view === 'week' && data && (
              <WeekItemsPanel
                plow={data.plow}
                weekStart={data.week_start}
                items={data.items}
                canEdit={canEdit}
                onChanged={loadWeek}
              />
            )}
          </div>
        </>
      )}

      {requestFor && (
        <RequestAccessModal
          plow={requestFor}
          onClose={() => setRequestFor(null)}
          onSent={(id) => {
            setRequestFor(null);
            toast.success('Request sent: the PLOW\'s editors have been told');
            setPlows(prev => (prev || []).map(p => p.id === id ? { ...p, request_pending: true } : p));
          }}
        />
      )}

      {setupOpen && (
        <SetupModal
          people={people}
          existing={setupConfig}
          currentUser={currentUser}
          onLevelsChanged={loadWeek}
          onClose={() => setSetupOpen(false)}
          onSaved={(id) => {
            setSetupOpen(false);
            toast.success(setupConfig ? 'PLOW updated' : 'PLOW created');
            loadPlows(id).then(() => { if (id === activeId) loadWeek(); });
          }}
        />
      )}

      {jobsOpen && data && (
        <JobsModal
          plowId={data.plow.id}
          jobs={data.jobs}
          onClose={() => setJobsOpen(false)}
          onChanged={loadWeek}
        />
      )}

      {exportOpen && data && (
        <ExportReportModal
          data={data}
          onClose={() => setExportOpen(false)}
          onDownloaded={(n) => {
            setExportOpen(false);
            toast.success(`Report downloaded: ${n} job${n === 1 ? '' : 's'} for ${fmtWeek(data.week_start)}`);
          }}
        />
      )}

      {clearOpen && data && (
        <ClearDaysModal
          data={data}
          members={visibleMembers}
          onClose={() => setClearOpen(false)}
          onCleared={({ cleared, standby_cancelled: standby }) => {
            setClearOpen(false);
            toast.success(`Cleared ${cleared} day${cleared === 1 ? '' : 's'} from ${fmtWeek(week)}${standby ? `, ${standby} standby slot${standby === 1 ? '' : 's'} cancelled` : ''}`);
            loadWeek();
          }}
        />
      )}

      {standbyWeekOpen && data && (
        <AssignStandbyWeekModal
          data={data}
          onClose={() => setStandbyWeekOpen(false)}
          onSaved={({ days, hours, member, level }) => {
            setStandbyWeekOpen(false);
            toast.success(`${personLabel(member || {})} on ${level?.name || 'standby'} for ${days} day${days === 1 ? '' : 's'} (${hours}h, £${standbyPay(hours)}), ${fmtWeek(week)}`);
            loadWeek();
          }}
        />
      )}

      {standbyCalendarOpen && data && (
        <StandbyCalendarModal
          data={data}
          initialMonth={week.slice(0, 7)}
          currentUserId={currentUser?.id}
          onClose={() => { setStandbyCalendarOpen(false); if (canEdit) loadWeek(); }}
        />
      )}

      {rotaOpen && data && (
        <ExportStandbyRotaModal
          data={data}
          currentUser={currentUser}
          onClose={() => setRotaOpen(false)}
          onDownloaded={(n, from, to, format) => {
            setRotaOpen(false);
            toast.success(format === 'upload'
              ? `On-call upload downloaded: ${fmtLongDate(from)} to ${fmtLongDate(to)}`
              : `Standby rota downloaded: ${n} slot${n === 1 ? '' : 's'}, ${fmtLongDate(from)} to ${fmtLongDate(to)}`);
          }}
        />
      )}

      {cellDetail && data && (
        <CellDetailModal
          data={data}
          rowId={cellDetail.rowId}
          date={cellDetail.date}
          jobById={jobById}
          absence={absenceMap[`${cellDetail.rowId}|${cellDetail.date}`]}
          onClose={() => setCellDetail(null)}
        />
      )}

      {cellEdit && data && !isLabelRow(data.members.find(m => rowKey(m) === cellEdit.rowId)) && (
        <CellEditorModal
          data={data}
          rowId={cellEdit.rowId}
          date={cellEdit.date}
          people={people}
          onClose={() => setCellEdit(null)}
          onSaved={() => { setCellEdit(null); loadWeek(); }}
          onRefresh={loadWeek}
        />
      )}
      {cellEdit && data && isLabelRow(data.members.find(m => rowKey(m) === cellEdit.rowId)) && (
        <LabelTasksEditorModal
          data={data}
          rowId={cellEdit.rowId}
          date={cellEdit.date}
          onClose={() => setCellEdit(null)}
          onSaved={() => { setCellEdit(null); loadWeek(); }}
          onRefresh={loadWeek}
        />
      )}

      {tour.open && (
        // With no boards yet the person looking is the one about to create
        // them, so an empty page gets the full tour rather than the viewer cut.
        <FeatureTour steps={plowTourSteps} context={{ canEdit: canEdit || (plows || []).length === 0, hasStandby: standbySectionIds.size > 0 }} onFinish={tour.finish} onSkip={tour.skip} />
      )}
    </div>
  );
}

// ── My month: the signed-in person's row, a line per week ─────────────────────
// Only the open board, read a week at a time through the same endpoint as the
// grid, so "current week only" still applies: a week not yet published to this
// person comes back refused and shows as such rather than failing the month.
// A day opens its week, where the cell can be read in full or edited.
function MyMonthGrid({ plow, month, personId, onOpenWeek }) {
  const [weeks, setWeeks] = useState(null);
  useEffect(() => {
    let live = true;
    setWeeks(null);
    Promise.all(monthWeekStarts(month, !!plow.include_weekends).map(start =>
      api.get(`plows/${plow.id}/week`, { start })
        .then(data => ({ start, data }))
        .catch(e => ({ start, error: e.message || 'This week could not be loaded' }))))
      .then(r => { if (live) setWeeks(r); });
    return () => { live = false; };
  }, [plow.id, plow.include_weekends, month]);

  if (!weeks) return <p className="text-[10px] text-gray-400 p-4">Loading…</p>;
  const loaded = weeks.filter(w => w.data);
  const onBoard = loaded.some(w => w.data.members.some(m => m.person_id === personId));
  if (loaded.length && !onBoard) {
    return <p className="text-[10px] text-gray-400 p-4">You do not have a row on {plow.name}, so there is nothing of yours to show. Switch to Week to see the whole board.</p>;
  }
  const days = plow.include_weekends ? 7 : 5;
  const dayNames = Array.from({ length: days }, (_, i) => fmtDay(addDays(weeks[0].start, i)));
  const gridCols = {
    gridTemplateColumns: `minmax(90px, 0.8fr) ${dayNames
      .map((_, i) => i >= 5 ? 'minmax(44px, 0.45fr)' : 'minmax(96px, 1fr)').join(' ')}`,
  };
  const today = todayIso();
  return (
    <div style={{ minWidth: `${100 + (days === 7 ? 5 * 96 + 2 * 44 : 5 * 96)}px` }}>
      <div className="grid border-b border-gray-200 bg-gray-50" style={gridCols}>
        <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wide px-3 py-2">Week</div>
        {dayNames.map((n, i) => (
          <div key={n} className={`px-1 py-2 border-l border-gray-200 text-center text-[8px] font-bold uppercase text-gray-400 ${i >= 5 ? 'bg-gray-100' : ''}`}>{n}</div>
        ))}
      </div>
      {weeks.map(({ start, data, error }) => {
        const dates = Array.from({ length: days }, (_, i) => addDays(start, i));
        const jobById = Object.fromEntries((data?.jobs || []).map(j => [j.id, j]));
        const mine = (data?.slots || []).filter(s => s.person_id === personId);
        return (
          <div key={start} className="grid border-b border-gray-100 last:border-b-0" style={gridCols}>
            <button type="button" onClick={() => onOpenWeek(start)} title="Open this week"
              className="px-3 py-2 text-left text-[10px] font-semibold text-gray-700 border-r border-gray-200 hover:bg-blue-50">
              WC {fmtShort(start)}
            </button>
            {!data ? (
              <div className="px-3 py-2 text-[9px] text-gray-400 flex items-center" style={{ gridColumn: `span ${days}` }}>{error}</div>
            ) : dates.map(d => (
              <div key={d} className={`flex flex-col min-w-0 ${d.slice(0, 7) === month ? '' : 'opacity-40'}`}>
                <span className={`text-[8px] font-bold px-1 pt-0.5 ${d === today ? 'text-blue-600' : 'text-gray-400'}`}>{fmtShort(d)}</span>
                <SlotCell
                  sections={data.sections}
                  slots={daySlots(data, (secId) => mine.find(s => s.slot_date === d && s.section_id === secId), jobById)}
                  isToday={d === today}
                  weekend={isWeekendIso(d)}
                  clickable
                  absence={(data.absences || []).find(a => a.person_id === personId && a.date === d)}
                  onClick={() => onOpenWeek(start)}
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
